/**
 * Veranstaltungs-Detail (Design-Update v2, Anhang E "Veranstaltungs-Detail Redesign"):
 * Status-Stepper + "Nächster Schritt"-Banner, Angebot, Abrechnung (Kostenübernahme/
 * Rechnungen/Zahlungen gebündelt), Aktivität (E-Mail-Verlauf + Verlauf gebündelt) links;
 * Eckdaten, Kunde, Aufgaben rechts. Alles gegen die Live-API.
 */
import { AnnahmeBelegeListe, type StoerenderBeleg } from "../ui/AnnahmeBelegeListe";
import { zahlwegText } from "../lib/zahlweg-text";
import { zahlungsSchrittArt, zahlungsstand, zahlungsstandMeta } from "../lib/zahlungsstand";
import { aLaCarteAnsEnde, istALaCarteOption, A_LA_CARTE_VOR_ORT_KOPF, A_LA_CARTE_ABRECHNUNG_VOR_ORT, istFassungNeuSendenMeldung } from "../lib/a-la-carte";
import { anlassAnzeige } from "../ui/format";
import { OpenTableAktion } from "../ui/OpenTableAktion";
import type { OpentableSicht } from "../lib/opentable";
import { heuteInZone } from "@maestro/feiertage";
import { feierVorbei } from "../lib/abrechnung-anzeige";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { useAngebotUebernahme } from "../hooks/useAngebotUebernahme";
import { useCompanyName } from "../hooks/useCompanyName";
import { openPrintableDocument, openPrintableDocumentPost } from "../lib/print";
import { openAuthenticatedFile } from "../lib/files";
import { currentMembership } from "../lib/membership";
import type { Customer, EventAttachment, EventRow, EventStatus, KundenBriefingResponse, Location, Me, OfferItem, OfferOption, Payment } from "../ui/types";
import {
  EVENT_STATUS,
  betriebsseitigeZusage,
  daysUntil,
  formatDate,
  formatDateTime,
  formatWeekdayDate,
  istVeranstaltungstag,
  money,
  PAYMENT_TYPE_LABEL,
  PILL_CLASSES,
  type PillTone,
  type StatusMeta,
} from "../ui/format";
import { ZahlungsplanAnpassenDrawer } from "../ui/auftrag/ZahlungsplanAnpassenDrawer";
import { RechnungOffenHinweis } from "../ui/auftrag/RechnungOffenHinweis";
import { RechnungSendenDialog } from "../ui/RechnungSendenDialog";
import { rechnungOffenGrund } from "../lib/rechnung-offen";
import { DankmailFehler } from "../ui/auftrag/DankmailFehler";
import { ratenOhneGueltigenLink, zahlungsplanHinweisNachAenderung } from "../lib/zahlungsplan-hinweis";
import {
  Aufklapper,
  Button,
  ButtonLink,
  Card,
  DateInput,
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  LoadingRows,
  MenueEintrag,
  MenueTitel,
  Modal,
  Radio,
  Rueckweg,
  Select,
  StatusPill,
  Zeichenzaehler,
  ZeitEingabe,
  useBestaetigung,
  ButtonRouterLink,
  useToastWennVorhanden,
} from "../ui/components";
import { VorgangKopf, titelAnzeige, type VorgangFakt } from "../ui/auftrag/VorgangKopf";
import { AktionsKarte, VorgangSchritte } from "../ui/auftrag/VorgangSchritte";
import { EckdatenUebersicht, uhrzeitText } from "../ui/auftrag/EckdatenUebersicht";
import { AdresseFenster, DatumFenster, GaesteFenster, KleinFenster, OrtFenster, StiftHinweis, TextFenster, UhrzeitFenster } from "../ui/auftrag/KleinFenster";
import { useMitHerkunft, useSeitenKontextMelden, vorgangsListe } from "../lib/rueckweg";
import { useContentRefetch } from "../ui/CopilotProvider";
import { VersionVorschauDrawer, type OfferHistoryEntry } from "../ui/auftrag/VersionVorschauDrawer";
import { AuftragVerlaufKarte } from "../ui/auftrag/AuftragVerlaufKarte";
import { leiteAuftragsVerlaufAb } from "../lib/auftrag-verlauf";
import { ErinnerungSendenDialog } from "../ui/ErinnerungSendenDialog";
import { GueltigkeitVerlaengernDialog } from "../ui/GueltigkeitVerlaengernDialog";
import { datumDe as gueltigDatumDe, gueltigkeitAbgelaufen } from "../lib/gueltigkeit";
import { SprachWahl } from "../ui/auftrag/SprachWahl";
import { useVarianteWahl } from "../ui/VarianteWaehlenDialog";
import { TelefonischAngenommenDialog, type TelefonischAngenommenErgebnis } from "../ui/TelefonischAngenommenDialog";
// Punkt 84: derselbe Drawer, den auch der Kopf der Verlaufs-Karte oeffnet — nicht neu gebaut.
import { AngebotsVerlaufDrawer } from "../ui/AngebotsVerlauf";
import { versandZeitpunkte } from "../lib/versand-zeitpunkte";
import { VerschiebenDialog, type VerschiebenSendeErgebnis } from "../ui/VerschiebenDialog";
import { AbsageKostenuebernahmeDialog, type KostenuebernahmeLage } from "../ui/AbsageKostenuebernahmeDialog";
import type { KostenuebernahmeEntscheidung } from "../lib/activity-labels";
import type { ApiError } from "../lib/api";
import { GewonnenKonfetti } from "../ui/GewonnenKonfetti";
import { wetterAnzeige, type WetterAnzeige, type WetterFolgerung, type WetterGrund } from "@maestro/wetter-anzeige";
import { standZeile } from "@maestro/recherche-stand";
import { deckungsSatz, type Deckung } from "../lib/zahlungsdeckung-anzeige";
import { tageBisEvent, ermittleKeepPercent, type CancellationTier } from "../lib/storno-vorschlag";
import { sortiereVariantenNachPreis } from "@maestro/pricing";
import { erstattungsRueckfallHinweis, erstattungsRueckfallZiel, kennzahlenFuerVorgang } from "../lib/zahlungs-kennzahlen";
import { ortTypChip, ortFehltHinweis } from "../lib/ort-typ";
import { abrechnungPfad, angebotPfad, angebotSendenPfad, angebotsseitePfad, BUCHUNGEN_PFAD, vorgangPfad } from "../lib/pfade";
import { FASSUNG_NEU_SENDEN } from "../lib/a-la-carte";
import { ZUSAGE_NEUE_FASSUNG_HINWEIS, ZUSAGE_NEUE_FASSUNG_KNOPF, ZUSAGE_NEUE_FASSUNG_TITEL } from "../lib/zusage-neue-fassung";
import { AdresseFelder, type AdresseFeld, type AdresseWerte } from "../ui/AdresseFelder";
import { LAENDER } from "../lib/laender";
import { adresseEinzeilig } from "../lib/adresse-anzeige";
import { landChip } from "../lib/land-hinweis";
import { AuftragKennzahlen } from "../ui/auftrag/AuftragKennzahlen";
import { FehlerOderHinweis } from "../ui/FassungNeuSendenHinweis";
import { SperreVorOrt } from "../ui/SperreVorOrt";
import { sperreAusFehler, type SperrLage } from "../lib/sperren";
import { RestzahlungAnlegenKnopf, zeigeRestzahlungKnopf } from "../ui/auftrag/RestzahlungAnlegenKnopf";
import { RestzahlungHinweis, ZahlungslinkVersand, type RestzahlungHinweisDaten } from "../ui/auftrag/ZahlungslinkVersand";
import type { CostAcceptance } from "../ui/auftrag/KostenuebernahmeDetail";
import { AuftragEckdatenKarte } from "../ui/auftrag/AuftragEckdatenKarte";
import { AngefragteTageKarte } from "../ui/AngefragteTageKarte";
import { FeldVersionenDrawer } from "../ui/auftrag/FeldVersionenDrawer";
import { buendleFuerVerlaufskarte, feldLabel, type FeldVersionZeile } from "../lib/feld-versionen";
import { AuftragGespraechKarte } from "../ui/auftrag/AuftragGespraechKarte";
import { AuftragNotizenKarte } from "../ui/auftrag/AuftragNotizenKarte";
import { AngebotErzeugungModal } from "../ui/auftrag/AngebotErzeugungModal";
import { DublettenHinweisKarte } from "../ui/auftrag/DublettenHinweisKarte";
import { KundenhistorieBlock } from "../ui/KundenhistorieBlock";
import { FirmenprofilKarte } from "../ui/FirmenprofilKarte";
import { AnfrageAngabenKarte } from "../ui/AnfrageAngabenKarte";
import { anfrageNummer, monatKurzText, wunschMonatVon } from "../lib/anfrage-angaben";
import { essensArt, type PaketArt } from "../lib/essens-art";
import { BewertungsbitteSchalter } from "../ui/BewertungsbitteSchalter";
import { MenuewahlNachBuchungKarte } from "../ui/MenuewahlNachBuchung";
import { AutomatischErkanntChip, KUNDENTYP_ICON, KUNDENTYP_TEXT, kundentypVon } from "../ui/Kundentyp";

/** Ziel des Sprungs "Betriebsadresse ergänzen" (§ 4.5). Dieselbe Deep-Link-Form wie
 *  `MODUL_KACHEL_URL` in customer-detail.tsx (PR #411) — settings.tsx liest `?tab=` aus der URL.
 *  Der Tab "Betrieb" ist der Ort, an dem die Adresse tatsächlich gepflegt wird (Karte
 *  „Firmen-Kopf"); die Wetter-Modul-Kachel unter `?tab=module` enthält nur den Ein/Aus-Schalter. */
const BETRIEBSADRESSE_URL = "/einstellungen?tab=betrieb";

/** Wetter-Widget (Datenanreicherung Welle A) — Antwortform von GET /api/events/:id/weather. */
interface DayForecast {
  date: string;
  description: string | null;
  icon: string | null;
  tempMinC: number | null;
  tempMaxC: number | null;
  precipitationMm: number | null;
}

// Backlog #41: `EventAttachment` (Chat-Anhang aus events.details.attachments) lag bis 16.09.2026
// hier lokal; seit der Dokumente-Reiter der Abrechnung dieselben Anhaenge spiegelt, steht die Form
// in `ui/types.ts` — eine Quelle, kein zweiter Speicher-/Typbegriff.

/**
 * Hard-Delete (30.08.2026): schmale Sicht auf GET /api/events/:id/invoices — nur die Felder, die
 * das Warn-Modal auflistet. Geladen erst, wenn das Modal offen ist (bedingtes useApi). Die
 * Kostenübernahme fürs selbe Modal kommt seit WP2 (08.09.2026) aus `caQ` oben, das jetzt immer
 * lädt (Kennzahlen-Kopf), nicht mehr aus einer eigenen, nur-fürs-Modal geladenen Abfrage.
 */
interface HardDeleteInvoiceInfo {
  id: string;
  invoiceType: string;
  invoiceNumber: string | null;
  status: string;
}
/** Client-seitiger Spiegel von apps/api/src/lib/weather.ts#isWithinForecastWindow (~ -1 bis +10
 *  Tage) -- rein, damit ausserhalb des Fensters gar kein Request an die Wetter-Route abgeht. */
function isWithinForecastWindowClient(dateStr: string): boolean {
  const target = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return false;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffDays = Math.round((target.getTime() - today) / 86_400_000);
  return diffDays >= -1 && diffDays <= 10;
}

/**
 * WP3b (Migration 385): baut die STRUKTURIERTE Lieferadresse als Freitext, nur wenn ALLE DREI
 * Felder gesetzt sind (dieselbe „alle drei oder keins"-Regel wie serverseitig
 * `lib/rechnungsadresse.ts#lieferadresseVollstaendig` — hier lokal nachgebildet, weil `apps/web`
 * keinen Import aus `apps/api` ziehen kann). `null` sonst, damit der Anfrage-Freitext-Fallback
 * greift (s. Aufrufer).
 */
function lieferadresseStructured(ev: { deliveryAddressStreet?: string | null; deliveryAddressZip?: string | null; deliveryAddressCity?: string | null } | null | undefined): string | null {
  const street = ev?.deliveryAddressStreet?.trim();
  const zip = ev?.deliveryAddressZip?.trim();
  const city = ev?.deliveryAddressCity?.trim();
  if (!street || !zip || !city) return null;
  return `${street}, ${zip} ${city}`;
}

/**
 * "Adresse dieses Auftrags" (`eventAddress*`) als `AdresseWerte` fuer den kontrollierten
 * `editAdresse`-State im "Vorgang bearbeiten"-Drawer. PR4-Neuanlage-Default (WARTESCHLANGE
 * Punkt 7, Konzept-Dokument § 4): ist die GESAMTE Auftragsadresse noch leer (kein einziges Feld
 * gesetzt — eine echte Neuanlage, nicht ein Bestandsfall mit nur fehlendem Land), wird
 * `countryCode` auf "DE" vorbelegt, reine UX-Vorbelegung im lokalen Formular-State. Ein
 * BESTEHENDER, unvollstaendiger Adressstand (z. B. Strasse gesetzt, Land fehlt - der Dubai-Fall)
 * bleibt UNVERAENDERT `null` (kein Raten fuer echte Bestandsfaelle, s. `landFehlt()`/Land-Chip
 * daneben) - nur eine komplett leere Adresse gilt als "Neuanlage".
 */
function adresseAusEvent(ev: { eventAddressCompany?: string | null; eventAddressName?: string | null; eventAddressStreet?: string | null; eventAddressAddition?: string | null; eventAddressZip?: string | null; eventAddressCity?: string | null; eventAddressCountryCode?: string | null } | null | undefined): AdresseWerte {
  const w: AdresseWerte = {
    company: ev?.eventAddressCompany ?? null,
    name: ev?.eventAddressName ?? null,
    street: ev?.eventAddressStreet ?? null,
    addition: ev?.eventAddressAddition ?? null,
    zip: ev?.eventAddressZip ?? null,
    city: ev?.eventAddressCity ?? null,
    countryCode: ev?.eventAddressCountryCode ?? null,
  };
  const komplettLeer = !w.company && !w.name && !w.street && !w.addition && !w.zip && !w.city && !w.countryCode;
  return komplettLeer ? { ...w, countryCode: "DE" } : w;
}

/** M-09: Persistierte Storno-Todos (events.details.cancellationTodos) — überleben Reload und Nutzerwechsel. */
interface CancellationTodos {
  cancelledAt?: string;
  canceledPayments?: number;
  openInvoices?: { id: string; invoiceNumber: string | null; invoiceType: string }[];
  paidPayments?: { id: string; amountCents: number }[];
}
/** Versand-Snapshot (offer_history) — Typen + Formatierhilfen + der Read-Only-Vorschau-Drawer
 *  selbst wanderten (WP0, Auftrags-Dashboard-Fundament, 08.09.2026, reines Refactoring) nach
 *  `ui/auftrag/VersionVorschauDrawer.tsx` (`OfferHistoryEntry` etc., `SEKTIONS_LABEL`,
 *  `mengeUndPreis`) — hier importiert, s. oben. */

interface ActivityEntry {
  id: string;
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}
/** Spiegel der Server-Whitelist (lib/event-status.ts) fuer die Aktions-Buttons. */
const ALLOWED: Record<EventStatus, EventStatus[]> = {
  // "offer_chosen" direkt aus "inquiry"/"offer_draft" (Konzept 2.3): telefonisch zugesagte/bar
  // bezahlte Deals ohne digitales Angebot.
  inquiry: ["offer_draft", "offer_sent", "offer_chosen", "no_response", "cancelled"],
  offer_draft: ["offer_sent", "offer_chosen", "cancelled"],
  offer_sent: ["offer_chosen", "offer_declined", "no_response", "cancelled"],
  offer_chosen: ["paid", "cancelled"],
  offer_declined: ["offer_draft", "cancelled"],
  paid: ["completed", "cancelled", "payment_failed", "refunded", "balance_due"],
  payment_failed: ["paid", "cancelled", "refunded", "balance_due"],
  // „Restforderung offen“ nach Teilerstattung (Migration 726) — Spiegel von lib/event-status.ts.
  balance_due: ["paid", "cancelled", "refunded"],
  // Punkt 93: „Erstattet“ nach voller Rueckerstattung — Spiegel von lib/event-status.ts.
  refunded: ["paid", "cancelled"],
  // Bugfund 11.09.2026 (Mimmo, ueber Antoine): "Abgelehnt" fehlte als Uebergang aus "Keine
  // Antwort" - Spiegel von lib/event-status.ts (dort ebenfalls ergaenzt).
  no_response: ["offer_draft", "cancelled", "offer_declined"],
  completed: [],
  // Punkt 98 „Absage zurücknehmen“ — Spiegel von lib/event-status.ts; Rolle, Pflichtbegründung,
  // Angebotsvariante und Deckung prüft der Server.
  cancelled: ["offer_draft", "offer_sent", "offer_chosen", "paid"],
};

/** "Nächster Schritt"-Text je Status (Anhang E, Card L1) — reine Empfehlung, keine Automatisierung. */
const NEXT_STEP_COPY: Partial<Record<EventStatus, string>> = {
  inquiry: "Anfrage prüfen und ein Angebot erstellen",
  offer_draft: "Angebot fertigstellen und an den Kunden senden",
  offer_sent: "Rückmeldung des Kunden einholen",
  offer_declined: "Neues Angebot erstellen oder Vorgang schließen",
  offer_chosen: "Rechnung/Zahlung einrichten",
  payment_failed: "Zahlung klären",
  balance_due: "Restbetrag beim Kunden anfordern",
  paid: "Durchführung vorbereiten",
  no_response: "Nachfassen oder Status klären",
  cancelled: "Abgesagt – war das ein Irrtum, über „⋯“ → Status ändern zurücknehmen",
};

const STEPPER: EventStatus[] = ["inquiry", "offer_draft", "offer_sent", "offer_chosen", "paid", "completed"];
const SCHRITTE = STEPPER.map((s) => ({ key: s, label: EVENT_STATUS[s].label }));

/*
 * Bugfund 11.09.2026 (Mimmo) / Punkt 98: Status außerhalb der linearen Kette (`no_response`,
 * `offer_declined`, `payment_failed`, `refunded`, `cancelled`) brauchen trotzdem eine Aktionskarte
 * und „Status ändern“ — seit „Salon“ steht „Status ändern“ immer im Menü „⋯“ und die Aktionskarte
 * gibt es für jeden Status außer „Durchgeführt“; der Stepper hebt für sie keinen Schritt hervor.
 */

/**
 * Design-System-Migration P2 (`event-detail.tsx`): Sentinel fuer die drei Selects im
 * Bearbeiten-Drawer mit einer echten, jederzeit erneut waehlbaren leeren Option
 * (`locationType`/`offSiteMode`/`locationId` — "Nicht gepflegt"/"Kein Ort ausgewählt"). Radix
 * `Select.Item` erlaubt keinen leeren `value`-String — dasselbe Muster wie `KEIN_WERT` in
 * `pages/settings.tsx`/`MANDANTEN_STANDARD` in `ui/NachschlagenTabs.tsx`.
 */
const KEIN_WERT = "__kein_wert__";


/** Eingehende/ausgehende Mails eines Vorgangs (`GET /api/events/:id/emails`) — Eingabe fuer
 *  `leiteAuftragsVerlaufAb` (WP1, `lib/auftrag-verlauf.ts`) UND fuer die reichere Darstellung
 *  unabhaengiger Mail-Zeilen in `ui/auftrag/AuftragVerlaufKarte.tsx` (Zustellstatus, Fehlertext,
 *  Retry-Buendelung — Bughunt 28.08.2026). Die eigene "Aktivität"-Feed-Kachel, die vorher direkt
 *  hier auf dieser Seite lebte, ist mit WP1 in der neuen `AuftragVerlaufKarte` aufgegangen. */
interface EmailRow {
  id: string;
  direction: "inbound" | "outbound";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  bodyText: string | null;
  /** Punkt 99/„Gespräch"-Karte: HTML-Faellback fuer Mails ohne Text-Teil. */
  bodyHtml?: string | null;
  status: string | null;
  error: string | null;
  aiClassification: string | null;
  receivedAt: string;
  /** Ungelesen-Status (`emails.read_at`): `null` = ungelesen. */
  readAt?: string | null;
  createdAt?: string | null;
  /** „Gespräch"-Karte (28.09.2026): Date-Header fuer die echte Sendezeit (`lib/gespraech.ts`) —
   *  die Route liefert das Feld ohnehin (`SELECT *`), hier nur bisher nicht typisiert. */
  rawHeaders?: unknown;
  /** „Gespräch"-Karte: Anhang-Kacheln. */
  attachmentsManifest?: { filename: string | null; mimeType: string; sizeBytes: number }[] | null;
  /** „Gespräch"-Karte: Zustellstatus (Punkt 114, Brevo). */
  ereignisse?: { ereignis: string; erstes: string; letztes: string; anzahl: number }[];
  /** „Gespräch"-Karte (28.09.2026, Zuordnung): Kanal fuer das WhatsApp-Kennzeichen — die Route
   *  liefert das Feld ohnehin (`SELECT *`), hier nur bisher nicht typisiert. */
  channel?: "email" | "whatsapp";
}

/**
 * E0.8: die in der Praxis wiederkehrenden Gründe. Bewusst kurz und ohne Wertung - sie sollen
 * das Eintippen ersparen, nicht die Antwort vorgeben. `Anderer Grund` mit Freitext steht
 * gleichberechtigt daneben.
 */
const DECLINE_GRUENDE = [
  "Zu teuer",
  "Termin anderweitig vergeben",
  "Gast hat woanders gebucht",
  "Veranstaltung abgesagt",
  "Keine Rückmeldung mehr",
] as const;

/**
 * Die Nein-Fälle der Wetter-Karte (R0.11, Konzept § 4.5): EIN Satz je Zustand, daneben höchstens
 * EINE Handlung — und nur dort, wo es einen Weg gibt.
 *
 * Diese Komponente entscheidet nichts. Zustand, Satz, Handlung und die Ort-Typ-Vermutung kommen
 * aus `wetterAnzeige()` in `@maestro/wetter-anzeige` — einer reinen Funktion, die dort getestet
 * wird, wo Tests laufen (`apps/web` hat keinen Test-Runner). Vorher stand hier ein Baum aus vier
 * Zweigen, dessen letzter jede Ursache zu „Keine Wetterdaten verfügbar." einebnete: fehlende
 * Adresse, nicht verortbare Adresse und ein Ausfall des DWD sahen identisch aus (Befund B4).
 *
 * Die Vermutung ist eine ANZEIGE: sie beschriftet den Weg ins Formular, füllt es nicht vor und
 * schreibt nichts. `events.location_type` bleibt unverändert, bis ein Mensch speichert.
 */
function WetterMeldung({
  anzeige,
  onBearbeiten,
  onErneutVersuchen,
}: {
  anzeige: WetterAnzeige;
  onBearbeiten: () => void;
  onErneutVersuchen: () => void;
}) {
  const linkClass = "font-label-md text-label-md text-primary hover:underline";
  return (
    <div className="space-y-2">
      <p className="font-body-sm text-body-sm text-on-surface-variant/70">{anzeige.satz}</p>
      {anzeige.vermutung && (
        <p className="rounded-lg border border-dashed border-outline-variant/60 bg-surface-container-low px-3 py-2 font-body-sm text-[11.5px] italic text-secondary dark:border-outline/40 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
          {anzeige.vermutung.satz}
        </p>
      )}
      {anzeige.handlung && (
        <p>
          {anzeige.handlung.art === "event_bearbeiten" && (
            <button type="button" className={linkClass} onClick={onBearbeiten}>
              {anzeige.handlung.beschriftung}
            </button>
          )}
          {anzeige.handlung.art === "betriebsadresse" && (
            <Link to={BETRIEBSADRESSE_URL} className={linkClass}>
              {anzeige.handlung.beschriftung}
            </Link>
          )}
          {anzeige.handlung.art === "erneut_versuchen" && (
            <button type="button" className={linkClass} onClick={onErneutVersuchen}>
              {anzeige.handlung.beschriftung}
            </button>
          )}
        </p>
      )}
    </div>
  );
}

/** Eine Zeile der Kunden-Karte: Icon-Kachel im Ton, kleines Label, Wert. */
function KundenZeile({
  icon,
  ton,
  label,
  children,
  bearbeiten,
}: {
  icon: string;
  ton: PillTone;
  label: string;
  children: ReactNode;
  /** Direkt bearbeiten per Klein-Fenster (Antoine 28.09.2026). */
  bearbeiten?: (schliessen: () => void) => ReactNode;
}) {
  const inhalt = (
    <>
      <span className={`grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] ${PILL_CLASSES[ton]}`}>
        <Icon name={icon} className="text-[17px]" />
      </span>
      <span className="min-w-0 flex-1 text-[13px]">
        <span className="block text-[11.5px] font-semibold text-secondary-fixed-dim">{label}</span>
        <span className="block font-semibold text-on-background dark:text-inverse-on-surface">{children}</span>
      </span>
    </>
  );
  const zeile = "flex w-full items-start gap-3 py-2.5 text-left";
  return (
    <li className="border-t border-surface-container first:border-t-0 dark:border-white/[0.05]">
      {bearbeiten ? (
        <KleinFenster
          testId={`kunde-${label.toLowerCase()}`}
          ausloeser={({ offen, umschalten }) => (
            <button
              type="button"
              onClick={umschalten}
              aria-expanded={offen}
              title={`${label} bearbeiten`}
              data-testid={`kunde-${label.toLowerCase()}`}
              className={`${zeile} group -mx-2 rounded-lg px-2 hover:bg-surface-container-low dark:hover:bg-white/[0.04] ${offen ? "bg-surface-container-low dark:bg-white/[0.04]" : ""}`}
            >
              {inhalt}
              <StiftHinweis />
            </button>
          )}
        >
          {bearbeiten}
        </KleinFenster>
      ) : (
        <div className={zeile}>{inhalt}</div>
      )}
    </li>
  );
}

export default function EventDetailPage() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  // 26.10.2026: mehrere aktive Varianten ohne Wahl -> Server fragt nach, hier der Dialog dazu.
  const { mitVarianteWahl, dialog: varianteDialog } = useVarianteWahl();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const companyName = useCompanyName();
  /**
   * Kanban-"⋯"-Menue "Notiz hinzufuegen" (freigegebenes Mockup 11.09.2026,
   * https://claude.ai/code/artifact/d498e616-448e-4432-9247-c8608d335efe) — `?notiz=1` bringt die
   * Notizen-Karte weiter unten dazu, sich automatisch zu oeffnen. Gleiches additive Muster wie
   * `?merge=` in `customer-detail.tsx`: Query-Param sofort nach dem Lesen wieder aufraeumen,
   * sonst oeffnete ein Reload den Drawer erneut.
   *
   * Bughunt WARTESCHLANGE Punkt 20 (14.09.2026, Antoine: „Icon Notiz führt nicht direkt zur Notiz.
   * Korrigieren.") — ROOT CAUSE: `notizAutoOpen` wurde bisher laufend aus `searchParams`
   * abgeleitet. Der Lade-Guard weiter unten (`if (eventQ.loading) return <LoadingRows .../>;`)
   * haelt JEDE Ausgabe dieser Seite zurueck, bis `event` geladen ist — `AuftragNotizenKarte`
   * (Zeile ~1903) montiert also erst NACH dem Laden. Der Aufraeum-Effekt direkt darunter haengt
   * dagegen nur an `notizAutoOpen`, nicht an `event`/`eventQ.loading` — er feuert schon beim
   * ALLERERSTEN Commit dieser Seite, lange bevor die Netzwerkantwort da ist, und entfernt
   * `?notiz=1` aus der URL, WAEHREND `AuftragNotizenKarte` noch gar nicht existiert. Montiert die
   * Karte dann endlich (nach dem Laden), liest sie `autoOpen` aus dem inzwischen laengst
   * bereinigten `notizAutoOpen` (mittlerweile `false`) — ihr eigener `useEffect([autoOpen])` sieht
   * nie ein `true`, `openDrawer()` wird nie gerufen. Verifiziert per E2E-Regressionstest
   * (`apps/e2e/tests/anfragen-kanban.spec.ts`) mit gedrosseltem `GET /api/events/:id` — ohne den
   * Fix bleibt der Notizen-Drawer nach der Navigation zu, mit Fix oeffnet er sich zuverlaessig.
   *
   * Fix: der Wert wird EINMALIG beim ersten Rendern eingefroren (Lazy-Initializer, `useState`,
   * kein laufend aus `searchParams` abgeleiteter Wert mehr) — dieser eingefrorene Wert ueberlebt
   * den Race, unabhaengig davon, wann `event` tatsaechlich laedt und wann die URL bereinigt wird.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [notizAutoOpen] = useState(() => searchParams.get("notiz") === "1");
  useEffect(() => {
    // Nur `notiz` entfernen — eine mitgebrachte Herkunft (`?von=`, Punkt 115) bleibt stehen.
    if (notizAutoOpen)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("notiz");
          return next;
        },
        { replace: true },
      );
    // Bewusst leeres Dep-Array: `notizAutoOpen` ist jetzt ein eingefrorener Anfangswert (s. o.),
    // kein laufend abgeleiteter — der Aufraeum-Effekt soll genau EINMAL beim Mount laufen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const eventQ = useApi<EventRow>(id ? `/api/events/${id}` : null);
  // Hard-Delete (30.08.2026): der Weg ist NUR fuer den Inhaber sichtbar (der Server prueft die
  // Rolle ohnehin nochmal serverseitig — der Knopf ist Komfort, kein Schutz).
  const meQ = useApi<Me>("/api/me");
  const isOwner = currentMembership(meQ.data)?.role === "owner";
  const offersQ = useApi<OfferOption[]>(id ? `/api/events/${id}/offers` : null);
  // Kachel „Art“ (Mockup vorgangskopf-handy, 16.10.2026): Positionen der maßgeblichen Variante
  // (gewählt bzw. einzige aktive, wie `angebotQuelle` unten) und — nur wenn ein Paket drinsteckt —
  // die Paketarten. Bestehende GET-Routen, kein eigener Endpunkt.
  const artVarianten = (offersQ.data ?? []).filter((o) => o.isActive);
  const artVariante = artVarianten.find((o) => o.isChosen) ?? (artVarianten.length === 1 ? artVarianten[0] : null);
  const artPositionenQ = useApi<{ items: OfferItem[] }>(artVariante ? `/api/offers/${artVariante.id}/full` : null);
  const artMitPaket = (artPositionenQ.data?.items ?? []).some((i) => i.section === "menu" && i.packageId);
  const artPaketeQ = useApi<PaketArt[]>(artMitPaket ? "/api/packages" : null);
  // Z0.3: Die Deckung des Zahlungsplans haengt an der ANTWORT, nicht an einer Zeile — sie kommt
  // deshalb ueber `zusatz` herein (siehe hooks/useApi.ts). Gerechnet hat sie der Server
  // (`apps/api/src/lib/zahlungsdeckung.ts`); hier wird sie nur noch formuliert.
  const paymentsQ = useApi<Payment[], { deckung?: Deckung }>(id ? `/api/events/${id}/payments` : null);
  // WP2 (Auftrags-Dashboard-Kennzahlen, 08.09.2026): vorher nur bedingt fuer den Hard-Delete-
  // Warnhinweis geladen (`hardDeleteOffen && id`), jetzt IMMER — die Kostenuebernahme-Kachel im
  // Kennzahlen-Kopf braucht den Stand bei jedem Seitenaufruf, nicht erst beim Loeschen-Versuch.
  // Kostenuebernahme-Sichtbarkeit (Domenico/STORIA-Feedback, 07.09.2026): dieselbe Abfrage liefert
  // auch das signierte-Badge in der Abrechnungs-Kachel unten — Status und Signatur-Datum sind hier
  // ergaenzt, statt eine zweite Abfrage gegen dieselbe Route zu duplizieren.
  // A3 (13.09.2026): derselbe Aufruf liefert seither die VOLLE Antwortform (`CostAcceptance`) —
  // die Kennzahl-Kachel oeffnet jetzt ein Drawer mit der kompletten Darstellung
  // (`ui/auftrag/KostenuebernahmeDetail.tsx`), nicht nur Status + Signaturdatum. Keine zweite
  // Abfrage: die Route lieferte diese Felder schon immer, die Seite hat sie nur weggeworfen.
  const caQ = useApi<CostAcceptance | null>(id ? `/api/events/${id}/cost-acceptance` : null);
  const event = eventQ.data;
  const mitHier = useMitHerkunft();
  // Punkt 115 (G4): Seitenleiste markiert die Liste, in der der Fall steht; oben steht sein Name.
  useSeitenKontextMelden(event ? vorgangsListe(event).to : null, event ? titelAnzeige(event.occasion ?? "Vorgang") : null);
  const customerQ = useApi<Customer>(event ? `/api/customers/${event.customerId}` : null);
  // Knopf „In OpenTable eintragen“ (26.10.2026): die Regeln entscheidet der Server; die Seite lädt neu,
  // sobald sich Status, Ort, Datum, Uhrzeit, Gästezahl oder die gewählte Variante ändern.
  const opentableQ = useApi<OpentableSicht>(id ? `/api/events/${id}/opentable` : null);
  const opentableStandKey = [
    event?.status, event?.locationType, event?.eventDate, event?.dateEnd, event?.timeFrom, event?.guestCount,
    (offersQ.data ?? []).map((o) => `${o.id}:${o.isChosen}:${o.isActive}:${o.guestCount}`).join(","),
  ].join("|");
  const opentableErsterStand = useRef(opentableStandKey);
  const opentableReload = opentableQ.reload;
  useEffect(() => {
    if (opentableErsterStand.current === opentableStandKey) return;
    opentableErsterStand.current = opentableStandKey;
    opentableReload();
  }, [opentableStandKey, opentableReload]);
  const [opentableBusy, setOpentableBusy] = useState(false);
  const [opentableFehler, setOpentableFehler] = useState<string | null>(null);
  async function opentableMarkieren(eingetragen: boolean) {
    if (!id) return;
    setOpentableBusy(true);
    setOpentableFehler(null);
    try {
      await apiMutate(`/api/events/${id}/opentable/eingetragen`, "PUT", { eingetragen });
      opentableReload();
      historyQ.reload();
    } catch (err) {
      setOpentableFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setOpentableBusy(false);
    }
  }
  const emailsQ = useApi<EmailRow[]>(id ? `/api/events/${id}/emails` : null);
  // Antoine 19.10.2026: Wer den Vorgang öffnet, hat das Gespräch gesehen — ungelesene Gast-Mails
  // gelten dann als gelesen, das Zeichen in den Anfragen verschwindet. Einmal je Vorgang (Ref),
  // auch wenn der Aufruf scheitert: keine Endlosschleife über das Neuladen.
  const gelesenMarkiertFuerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !emailsQ.data || gelesenMarkiertFuerRef.current === id) return;
    if (!emailsQ.data.some((m) => m.direction === "inbound" && m.readAt == null)) return;
    gelesenMarkiertFuerRef.current = id;
    void apiMutate("/api/inbox/mark-read", "POST", { eventId: id })
      .then(() => emailsQ.reload())
      .catch(() => {});
  }, [id, emailsQ.data]);
  const activityQ = useApi<ActivityEntry[]>(id ? `/api/events/${id}/activity` : null);
  const historyQ = useApi<OfferHistoryEntry[]>(id ? `/api/events/${id}/offer-history` : null);
  // PR4 (docs/PLAN-EVENT-FELD-VERSIONIERUNG.md Abschnitt 4.2 Regel C): Grundlage fuer die
  // verdichtete Eckdaten-Sammelzeile in der Verlaufs-Karte. Eigenstaendiger Fetch (nicht geteilt
  // mit `AuftragEckdatenKarte`s eigenem "geändert nach Zusage"-Diff oder `FeldVersionenDrawer`s
  // eigenem Chronik-Fetch) — bewusste, kleine Mehrfachabfrage statt eines groesseren Umbaus quer
  // durch drei Komponenten, s. Abschlussbericht der PR4-Umsetzung.
  const feldVersionenQ = useApi<FeldVersionZeile[]>(id ? `/api/events/${id}/field-versions?limit=200` : null);

  // A2c: Schreibaktionen des Copilot am Angebot (changed.resource === "offer", z. B. „Angebot
  // senden") lösen hier gezielt einen Refetch der Angebots-Ansicht aus — nur diese Seite.
  useContentRefetch("offer", () => {
    offersQ.reload();
    historyQ.reload();
  });
  // Chat-P1: Vorgangs-Status/-Felder (event_transition, update_event) per Chat -> Kopfbereich
  // dieser Detailseite live nachladen. Die "task"-Ressource (create/update_task per Chat) wird
  // seit der Notiz-/Erinnerungsfunktion NICHT mehr hier, sondern direkt in
  // `AuftragNotizenKarte.tsx` abonniert (eigenstaendige Karte mit eigenem `useApi`, s. dort).
  //
  // F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): zusaetzlich "inquiry" abonniert — von
  // `pages/anfrage-detail.tsx` uebernommen (dort `useContentRefetch(["inquiry","event"], ...)`,
  // s. `apps/agent/test/knopf-und-fliessband.test.ts`). `angebot_komponieren`/
  // `angebot_manuell_erstellen` (maestro-agent.ts) melden nach einem Chat-Lauf NUR
  // `resource: "inquiry"` — ohne dieses Abo saehe man das gerade komponierte Angebot auf der
  // Vorgangsseite erst nach einem manuellen Reload.
  useContentRefetch(["inquiry", "event"], () => eventQ.reload());
  // Datenanreicherung Welle A: fehlende tenant_settings-Zeile -> {data: null} -> "alle Flags false"
  // (nie Fehler, nie Default-an).
  // F3/F4 (docs/KONZEPT-ABSAGE-STORNO-LEBENSZYKLUS.md): dieselbe Antwort traegt seit Migration 404
  // auch die strukturierte Stornostaffel (`cancellationTiers`, TENANT_SETTINGS_SICHTBAR) — kein
  // zweiter `useApi`-Aufruf gegen dieselbe Route noetig, nur der Typ ist hier breiter.
  const settingsQ = useApi<{ wetterEnabled?: boolean; cancellationTiers?: CancellationTier[] | null; companyEmail?: string | null } | null>(
    "/api/settings/payment-terms",
  );
  const wetterEnabled = settingsQ.data?.wetterEnabled ?? false;
  // WvonZ-Fund 20.10.2026: ohne LexOffice-Schlüssel gibt es keine Rechnung (Abrechnung blendet die
  // Knöpfe aus) — der „Nächster Schritt“-Text verspricht sie dann nicht. null/lädt = wie bisher.
  const moduleQ = useApi<{ lexofficeKeyHinterlegt?: boolean | null } | null>("/api/settings/module");
  const lexofficeKeyFehlt = moduleQ.data?.lexofficeKeyHinterlegt === false;
  // Koordinaten-Aufloesung laeuft serverseitig (GET .../weather) -- hier nur das ~10-Tage-Fenster
  // pruefen, damit ausserhalb davon gar kein Request abgeht.
  const weatherWindowOk = eventQ.data?.eventDate ? isWithinForecastWindowClient(eventQ.data.eventDate) : false;
  const shouldFetchWeather = Boolean(id && wetterEnabled && eventQ.data?.locationType && weatherWindowOk);
  // R0.11: Der zweite Typparameter holt das Feld NEBEN `data` aus dem Umschlag — den `reason` aus
  // R0.10 (PR #424). Ohne ihn verwarf `useApi` genau diese Angabe, und die Karte konnte weiterhin
  // nur "Keine Wetterdaten verfügbar." sagen.
  // R4.3: `folgerung` steht NEBEN `data` und kommt fertig vom Server (routes/weather.ts) — die
  // Schwellenwert-Regel liegt in @maestro/wetter-anzeige, damit Karte und Chat denselben Satz
  // zeigen. Hier wird nur gerendert, nichts entschieden.
  const weatherQ = useApi<DayForecast | null, { reason?: WetterGrund; folgerung?: WetterFolgerung }>(
    shouldFetchWeather ? `/api/events/${id}/weather` : null,
  );
  // Die EINE Stelle, an der aus der Datenlage ein Zustand wird (§ 4.5). Die Komponente rendert nur.
  const wetter = wetterAnzeige({
    ortTyp: eventQ.data?.locationType ?? null,
    hatTermin: Boolean(eventQ.data?.eventDate),
    imFenster: weatherWindowOk,
    laedt: shouldFetchWeather && weatherQ.loading,
    prognoseDa: Boolean(weatherQ.data),
    grund: weatherQ.zusatz?.reason ?? null,
    fehler: shouldFetchWeather ? weatherQ.error : null,
    // Signale der erzeugenden Anfrage — reine Anzeige, sie werden nie zurueckgeschrieben.
    serviceKind: eventQ.data?.serviceKind ?? null,
    // WP3b (Migration 385): die STRUKTURIERTE Lieferadresse (falls vollstaendig gepflegt) ist ein
    // staerkeres Signal als der Anfrage-Freitext und bekommt deshalb Vorrang — @maestro/wetter-
    // anzeige#ortTypVermutung nimmt ohnehin nur einen String entgegen, es aendert sich nichts an
    // der Regel selbst. Fehlt die strukturierte Adresse, bleibt der bisherige Freitext-Fallback.
    lieferadresse: lieferadresseStructured(eventQ.data) ?? eventQ.data?.deliveryAddress ?? null,
    // WP5c (Migration 386): ein explizit gepflegtes Aufbau-Feld ist ein staerkeres Signal als der
    // bisherige Text-Rateweg oben — s. `ortTypVermutung`-Kommentarkopf in @maestro/wetter-anzeige.
    setupLocation: eventQ.data?.setupLocation ?? null,
  });

  // M-09: Storno-Todos kommen persistiert aus events.details.cancellationTodos (der Server schreibt
  // sie atomar mit dem Storno) — sie überleben Reload/Tabwechsel und sind für alle Nutzer sichtbar.
  const cancellationTodos =
    event?.status === "cancelled"
      ? ((event.details as { cancellationTodos?: CancellationTodos } | null)?.cancellationTodos ?? null)
      : null;

  const [busy, setBusy] = useState(false);
  // Punkt 29 P2: Zahlungsplan-Hinweis nach geänderter Annahme — dasselbe Fenster wie auf der Abrechnung.
  const [planAnpassenOffen, setPlanAnpassenOffen] = useState(false);
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  // Einheit E: „Rechnung senden“ von Hand zu einer bezahlten Zahlung ohne versendete Rechnung.
  const [rechnungZahlung, setRechnungZahlung] = useState<Payment | null>(null);
  const [linkFehler, setLinkFehler] = useState<string | null>(null);
  const toast = useToastWennVorhanden();
  const [restzahlungHinweis, setRestzahlungHinweis] = useState<RestzahlungHinweisDaten | null>(null);
  useEffect(() => setRestzahlungHinweis(null), [event?.id]);
  const [historyBusy, setHistoryBusy] = useState<number | null>(null);
  /**
   * Hard-Delete (30.08.2026, Antoine: "Ja, mit scharfer Warnung"): endgueltiges Loeschen auch
   * nach Versand und auch mit Provider-Objekten — fuer Testauftraege. Das Modal listet auf, was
   * mitgeloescht wird, und verlangt die Vorgangsnummer zum Abtippen; der SERVER prueft Rolle und
   * Nummer nochmal (POST /api/events/:id/hard-delete, Muster Inhaberwechsel PR #803).
   */
  const [hardDeleteOffen, setHardDeleteOffen] = useState(false);
  const [hardDeleteInput, setHardDeleteInput] = useState("");
  const [hardDeleteBusy, setHardDeleteBusy] = useState(false);
  const [hardDeleteFehler, setHardDeleteFehler] = useState<string | null>(null);
  // Was haengt dran? Rechnungen werden erst beim Oeffnen des Modals geladen (Zahlungen und
  // offerSentAt hat die Seite ohnehin, den Kostenuebernahme-Vertrag seit WP2 ueber `caQ` oben,
  // das jetzt immer laedt statt nur bei geoeffnetem Modal).
  const hardDeleteInvoicesQ = useApi<HardDeleteInvoiceInfo[]>(hardDeleteOffen && id ? `/api/events/${id}/invoices` : null);
  /**
   * B2 (UX-Polish 25.08.2026): welche Version steht gerade in der Read-Only-Vorschau offen?
   * Das Versionsarchiv war bis hierher eine Liste, die NICHT sagt, was in einer Version stand —
   * nur „3 Optionen · 4.130,00 €". Wer wissen wollte, ob die alte Fassung die bessere war,
   * musste sie kopieren (und damit eine vierte Variante erzeugen), nachsehen und wieder
   * aufraeumen. Die Vorschau beantwortet die Frage, ohne etwas anzulegen.
   */
  const [vorschauVersion, setVorschauVersion] = useState<number | null>(null);
  // PR4: EINE Instanz fuer die gesamte Seite — sowohl das Banner "Geändert nach Zusage" in
  // `AuftragEckdatenKarte` als auch die Sammelzeile in `AuftragVerlaufKarte` oeffnen denselben
  // Drawer (Plan Abschnitt 4.1: "einziger Ort mit Zurücksetzen-Knopf").
  const [feldVersionenDrawerOffen, setFeldVersionenDrawerOffen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // S6: eine erwartbare Sperre (409 u. ä.) steht in der Aktionskarte, an der Aktion, die gescheitert ist - nicht im Sammelkasten oben.
  const [sperre, setSperre] = useState<SperrLage | null>(null);
  /** Meldet einen Aktionsfehler: Sperre -> Hinweis mit Ausweg in der Aktionskarte, echter Fehler -> wie bisher laut. */
  function aktionFehler(err: unknown, text?: (msg: string) => string) {
    const s = sperreAusFehler(err);
    if (s) {
      setActionError(null);
      setSperre(s);
      return;
    }
    const msg = err instanceof Error ? err.message : String(err);
    setActionError(text ? text(msg) : msg);
  }
  /** „Neu laden“ / „Fassung aktualisieren“ am Sperr-Hinweis: alles zum Vorgang frisch holen. */
  function sperreNeuLaden() {
    setSperre(null);
    setActionError(null);
    eventQ.reload();
    offersQ.reload();
    paymentsQ.reload();
    activityQ.reload();
  }

  /**
   * E2 (UX-Polish Paket E), seit 28.09.2026 die Automatik (Antoine: „Die Karte soll IMMER
   * automatisch ausgefuellt sein — der Betreiber soll nicht mehr klicken muessen").
   *
   * `GET /api/events/:id/kunden-briefing` liefert einen zwischengespeicherten Stand ohne
   * Modellaufruf, oder erzeugt EINMAL neu, wenn er fehlt/veraltet ist (Server-Entscheidung,
   * `kunden_briefing_cache`/`istBriefingVeraltet` — kein Kostenrisiko durch jeden Seitenaufruf,
   * dieselbe Sorge wie bei der alten Knopf-Loesung, nur serverseitig geloest statt per Klick).
   * Ganz gewoehnlicher `useApi`-Hook wie jede andere Karte der Seite — kein eigener Effekt noetig.
   */
  const briefingQ = useApi<KundenBriefingResponse>(event?.customerId ? `/api/events/${event.id}/kunden-briefing` : null);
  const briefing = briefingQ.data;
  // Nebenaktion "Neu erzeugen"/"Erneut versuchen": erzwingt eine Neuerzeugung unabhaengig vom
  // Cache-Zustand (POST), dann `reload()` — die GET-Antwort liest den frisch gespeicherten Stand,
  // ohne den Text ein zweites Mal ueber die Leitung zu schicken.
  const [briefingNeuBusy, setBriefingNeuBusy] = useState(false);
  const [briefingNeuFehler, setBriefingNeuFehler] = useState<string | null>(null);

  async function briefingNeuErzeugen() {
    if (!event) return;
    setBriefingNeuBusy(true);
    setBriefingNeuFehler(null);
    try {
      await apiMutate<KundenBriefingResponse>(`/api/events/${event.id}/kunden-briefing`, "POST");
      briefingQ.reload();
    } catch (e) {
      setBriefingNeuFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBriefingNeuBusy(false);
    }
  }

  const [printBusy, setPrintBusy] = useState(false);
  // 09.09.2026 (Antoine-Auftrag): "Erinnerung senden" oeffnet jetzt den Compose-Dialog statt
  // sofort zu senden - `reminderBusy` entfaellt, der Dialog fuehrt seinen eigenen Sende-Zustand
  // (s. `ErinnerungSendenDialog.tsx`).
  const [reminderOpen, setReminderOpen] = useState(false);
  /*
   * Punkt 84 (Antoine, 20.09.2026, mit Bildschirmfoto der markierten Versandzeile): „Der Ort wo
   * man sehen muss, wann Erinnerungen verschickt wurden und wie viele (Datum nur die letzte).
   * Klick darauf zeigt eine komplette Liste im Popup."
   *
   * Die Liste gibt es bereits — `AngebotsVerlaufDrawer` fuehrt je Nachfassung eine eigene Zeile.
   * Sie war von hier aus nur nicht erreichbar: der Weg dorthin lag ausschliesslich im Kopf der
   * Verlaufs-Karte weiter unten, und wer die Versandzeile las, musste wissen, dass es sie gibt.
   * Deshalb KEIN neues Fenster, sondern derselbe Drawer, von der Zeile aus geoeffnet. Eine
   * zweite Instanz ist unproblematisch: gerendert wird nur, was offen ist, und offen ist immer
   * hoechstens eine.
   */
  const [versandVerlaufOffen, setVersandVerlaufOffen] = useState(false);
  // § 8 (Migration 345, 31.08.2026): "Telefonisch angenommen".
  // Punkt 96 (23.09.2026): der Knopf oeffnet einen Dialog mit optionaler Gast-Mail.
  const [telefonischOffen, setTelefonischOffen] = useState(false);
  // Ausweg aus „Angebot abgelaufen“ (Antoine, 04.11.2026): „Gültig bis“ verlängern, ohne Neuversand.
  const [gueltigkeitOffen, setGueltigkeitOffen] = useState(false);
  // E0.8: Ablehnen fragt nach dem Grund. Die Auswahl ist eine Starthilfe, kein Zwang -
  // „Anderer Grund" mit Freitext ist gleichwertig, und Überspringen bleibt möglich.
  const [declineOpen, setDeclineOpen] = useState(false);
  /** WARTESCHLANGE Punkt 47: Rückfrage vor dem Zurücknehmen — die Zusage des Kunden gilt danach nicht mehr. */
  const [annahmeZurueckOffen, setAnnahmeZurueckOffen] = useState(false);
  // 04.11.2026: lebende Belege, die die Rücknahme der Annahme aufhalten — mit Weg je Beleg (Server: stoerendeBelege).
  const [annahmeBelege, setAnnahmeBelege] = useState<StoerenderBeleg[]>([]);
  /** Punkt 98: Ziel der „Absage zurücknehmen“-Rückfrage (null = zu) und die Pflichtbegründung. */
  const [absageZurueckZiel, setAbsageZurueckZiel] = useState<EventStatus | null>(null);
  const [absageZurueckGrund, setAbsageZurueckGrund] = useState("");
  const [declineWahl, setDeclineWahl] = useState("");
  const [declineFrei, setDeclineFrei] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  // Design-System-Migration P2: `locationId`/`offerLanguage` sind seit dem Umstieg auf
  // `<Select>` (Radix, s. KEIN_WERT oben) lokaler React-State statt per FormData ausgelesen —
  // Radix' Select erzeugt kein natives, FormData-taugliches Element (Muster settings.tsx).
  // `locationType`/`offSiteMode` sind seit WP3a (Auftrags-Dashboard) nicht mehr Teil dieses
  // Formulars (jetzt `AuftragEckdatenKarte`, eigenes Autosave dort). Reset bei jedem Oeffnen
  // des Drawers: das Drawer selbst unmountet den Formularinhalt nicht (Zustand lebt hier in
  // der Elternkomponente), daher der explizite `useEffect` auf `editOpen`.
  const [editLocationId, setEditLocationId] = useState(event?.locationId ?? KEIN_WERT);
  // PR4 (Adressmodell, WARTESCHLANGE Punkt 7): "Adresse dieses Auftrags" (eventAddress*) bekommt
  // mit dem Land-Feld ebenfalls einen Radix-`Select`-Anteil (s. Begruendung oben fuer
  // `editLocationId`/`editOfferLanguage`) - deshalb wandert das GANZE Feldset hier auf denselben
  // kontrollierten Reset-Zustand statt nur Land separat zu behandeln (sonst zwei verschiedene
  // Lese-Mechanismen fuer dieselbe Feldgruppe in `saveEdit`).
  const [editAdresse, setEditAdresse] = useState<AdresseWerte>(() => adresseAusEvent(event));
  useEffect(() => {
    if (editOpen) {
      setEditLocationId(event?.locationId ?? KEIN_WERT);
      setEditAdresse(adresseAusEvent(event));
    }
  }, [
    editOpen,
    event?.locationId,
    event?.eventAddressCompany,
    event?.eventAddressName,
    event?.eventAddressStreet,
    event?.eventAddressAddition,
    event?.eventAddressZip,
    event?.eventAddressCity,
    event?.eventAddressCountryCode,
  ]);
  function onAdresseFeldChange(feld: AdresseFeld, wert: string) {
    setEditAdresse((prev) => ({ ...prev, [feld]: wert }));
  }
  const locationsQ = useApi<Location[]>(editOpen ? "/api/locations" : null);
  // WP3a (Auftrags-Dashboard, 08.09.2026): Ort-Typ/OffSiteMode-Select, Termin, Gäste und der
  // Ort-Freitext sind aus diesem Bearbeiten-Drawer in die neue `AuftragEckdatenKarte` gewandert
  // (eigenes Autosave-onBlur/onChange dort, kein Sammel-Speichern-Knopf mehr für diese Felder) —
  // `editLocationType`/`ortBearbeiten`/`zeitBearbeiten` und ihre Speicher-Funktionen sind darum
  // entfallen, s. Kommentarkopf `ui/auftrag/AuftragEckdatenKarte.tsx`.
  /**
   * E3 („Salon“, 26.09.2026): die Eckdaten-Felder stehen im Drawer „Eckdaten“. Der Kopf („Ort
   * ergänzen“, fehlender Ort-Typ, fehlende Uhrzeit/Gäste) und „Bearbeiten“ in der Übersicht öffnen
   * ihn. A2 (13.09.2026) bleibt erhalten: kommt der Klick von „Ort ergänzen“, bekommt das Ort-Feld
   * den Fokus (`ortFokusToken` > 0), sonst nicht.
   */
  const [eckdatenOffen, setEckdatenOffen] = useState(false);
  const [ortFokusToken, setOrtFokusToken] = useState(0);
  function eckdatenOeffnen(ortFokus: boolean) {
    setOrtFokusToken(ortFokus ? (n) => n + 1 : 0);
    setEckdatenOffen(true);
  }

  // Punkt 97: Schnellweg Datum im Seitenkopf (Kalenderblatt, `VorgangKopf`), gespeichert beim
  // Verlassen des Feldes. Leer = Datum entfernen.
  async function kopfDatumSpeichern(wert: string) {
    if (!id || wert === (event?.eventDate?.slice(0, 10) ?? "")) return;
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}`, "PATCH", { eventDate: wert || null });
      eventQ.reload();
      weatherQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  // Klein-Fenster (Antoine 28.09.2026): Fehler wirft weiter, das Fenster zeigt die Ursache.
  async function vorgangPatch(patch: Record<string, unknown>) {
    if (!id) return;
    await apiMutate(`/api/events/${id}`, "PATCH", patch);
    eventQ.reload();
    weatherQ.reload();
    // Gästezahl: der Server zieht die Varianten mit (lib/event-patch.ts).
    offersQ.reload();
  }
  async function kundePatch(patch: Record<string, unknown>) {
    if (!event?.customerId) return;
    await apiMutate(`/api/customers/${event.customerId}`, "PATCH", patch);
    customerQ.reload();
  }

  async function saveEdit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    const form = new FormData(e.currentTarget);
    setEditBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}`, "PATCH", {
        // A-08: leer gelassene Felder als null senden (Server-Schema erlaubt nullable) statt sie
        // als undefined wegzulassen - sonst kann der Nutzer einen Wert nicht wieder loeschen.
        // WP3a (08.09.2026): eventDate/timeFrom/timeTo/guestCount/location/locationType/
        // offSiteMode sind aus diesem Formular entfallen (jetzt `AuftragEckdatenKarte`,
        // autosave onBlur/onChange dort) — hier NICHT mehr mitschicken, sonst wuerde ein
        // Anlass-Save diese Felder mangels Formularwert auf null zuruecksetzen.
        // A5/E2 (13.09.2026): `serviceType` wird hier NICHT mehr mitgeschickt — das Eingabefeld
        // ist aus dem Drawer entfernt (Begruendung dort), und ohne Formularwert wuerde jeder
        // Anlass-Save den vorhandenen Steuerwert auf null zuruecksetzen (dieselbe Falle wie bei
        // den WP3a-Feldern darueber). Die Spalte selbst bleibt, sie speist `resolvePillar`.
        occasion: (form.get("occasion") as string) || null,
        // Punkt 97 (Antoine, 23.09.2026): Datum + Uhrzeit wieder im Drawer, leer = null (entfernbar).
        eventDate: (form.get("eventDate") as string) || null,
        timeFrom: (form.get("timeFrom") as string) || null,
        timeTo: (form.get("timeTo") as string) || null,
        // Design-System-Migration P2: `locationId` kommt seit dem Select-Umstieg aus dem
        // kontrollierten `editLocationId`-State statt aus FormData — der haelt IMMER den
        // korrekten Wert (initial aus `event.locationId`, s. Reset-Effekt oben), unabhaengig
        // davon, ob `locationsQ.data` schon geladen ist. Die alte A-01-Bedingung schuetzte vor
        // einem Sonderfall des NATIVEN, `defaultValue`-basierten Auswahlfelds (fehlte die passende
        // Option beim ersten Render, waehlte der Browser die leere Option und `FormData` haette
        // den zugewiesenen Ort still auf null gesetzt) — der bei einem kontrollierten Radix-Select
        // strukturell entfaellt, darum jetzt unbedingt mitgeschickt.
        locationId: editLocationId === KEIN_WERT ? null : editLocationId,
        // Migration 350 (Auftragsadresse): dieselbe A-08-Regel - leer = null senden, damit eine
        // bereits gesetzte Auftragsadresse auch wieder entfernbar ist (dann gilt wieder die
        // Kundenadresse als Fallback). PR4 (Adressmodell): das GANZE Feldset kommt jetzt aus
        // `editAdresse` statt teils aus `FormData` (s. Kommentar am State oben) - Land ist ein
        // kontrollierter Radix-`Select` und kann strukturell nicht ueber `FormData` gelesen werden,
        // und ein gemischter Lesepfad (Firma/Name/Zusatz aus FormData, Land aus State) waere hier
        // fehleranfaelliger als ein einheitlicher.
        eventAddressCompany: editAdresse.company?.trim() || null,
        eventAddressName: editAdresse.name?.trim() || null,
        eventAddressStreet: editAdresse.street?.trim() || null,
        eventAddressAddition: editAdresse.addition?.trim() || null,
        eventAddressZip: editAdresse.zip?.trim() || null,
        eventAddressCity: editAdresse.city?.trim() || null,
        eventAddressCountryCode: editAdresse.countryCode?.trim() || null,
      });
      setEditOpen(false);
      eventQ.reload();
      // Migration 350: die Wetter-Karte haengt an derselben URL (kein locationType-/eventDate-
      // Wechsel noetig, um eine geaenderte Auftragsadresse zu sehen) - ohne den expliziten Reload
      // hier bliebe sie bis zum naechsten vollen Seitenaufruf auf dem alten Grund ("keine_adresse")
      // stehen, obwohl die Adresse laengst gespeichert ist. No-op, wenn die Karte gerade gar nicht
      // laedt (wetterEnabled aus, kein Termin/Ort-Typ) - reload() auf einem `null`-Pfad tut nichts.
      weatherQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditBusy(false);
    }
  }

  async function printKitchenSheet() {
    if (!id) return;
    setPrintBusy(true);
    setActionError(null);
    try {
      await openPrintableDocument(`/api/events/${id}/kitchen-sheet`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setPrintBusy(false);
    }
  }

  /** Angebots-Dokument (Block 2): druckfertige HTML-Ansicht einer Angebotsvariante. */
  async function printOfferDocument(offerId: string) {
    setPrintBusy(true);
    setActionError(null);
    try {
      await openPrintableDocument(`/api/offers/${offerId}/document`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setPrintBusy(false);
    }
  }

  /**
   * Versionsarchiv: eine gesendete Version als neuen Entwurf kopieren (nur mit Positions-Snapshot).
   *
   * B2 (25.08.2026): Der frueher vorgeschaltete `window.confirm` ist entfallen. Er stand da, weil
   * der Knopf blind aus der Liste heraus wirkte — man wusste nicht, WAS man kopiert. Jetzt wird
   * er nur noch aus der Vorschau ausgeloest, in der der Inhalt der Version vollstaendig
   * dasteht, und der Knopf sagt selbst, dass eine ZUSAETZLICHE Variante entsteht. Ein
   * Bestaetigungsdialog ueber einer Ansicht, die schon alles zeigt, ist eine Klickhuerde ohne
   * Erkenntnisgewinn — und genau die trainiert man sich weg.
   *
   * Der Weg selbst ist unveraendert: `POST /api/events/:id/offer-history/:version/restore`,
   * append-only (die bestehenden Varianten bleiben stehen, es kommt eine dazu).
   */
  async function restoreVersion(version: number) {
    if (!id) return;
    setHistoryBusy(version);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/offer-history/${version}/restore`, "POST", {});
      offersQ.reload();
      setVorschauVersion(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /snapshot_incomplete/i.test(msg)
          ? "Diese Version wurde vor der Positions-Archivierung versendet und enthält keine Positionen – nur als Zusammenfassung verfügbar."
          : /event_locked/i.test(msg)
            ? "Dieses Angebot ist nach der verbindlichen Annahme gesperrt – eine zusätzliche Variante würde die angenommene Fassung mehrdeutig machen."
            : msg,
      );
    } finally {
      setHistoryBusy(null);
    }
  }

  /**
   * Punkt 29 P2, Entscheidung 3: nach einer Plananpassung wird NICHTS automatisch verschickt. Erst
   * dieser Klick erzeugt den neuen Link (bestehende Route, H-30) und schickt ihn an den Kunden.
   */
  async function neuenZahlungslinkSenden(p: Payment) {
    const art = PAYMENT_TYPE_LABEL[p.paymentType] ?? "Zahlung";
    if (
      !(await bestaetigen({
        titel: "Neuen Zahlungslink senden?",
        text: `Für „${art}“ über ${money(p.amountCents)} wird ein neuer Zahlungslink erzeugt und an den Kunden geschickt.`,
        bestaetigen: "Link senden",
      }))
    )
      return;
    setLinkBusy(p.id);
    setLinkFehler(null);
    try {
      try {
        await apiMutate(`/api/payments/${p.id}/checkout-link`, "POST", {});
      } catch (err) {
        setLinkFehler(`Der Zahlungslink konnte nicht erzeugt werden: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
      try {
        const antwort = await apiMutate<{ to?: string } | undefined>(`/api/payments/${p.id}/send-link`, "POST", {});
        toast.success(antwort?.to ? `Zahlungslink an ${antwort.to} gesendet` : "Zahlungslink gesendet");
      } catch (err) {
        setLinkFehler(
          `Der neue Zahlungslink ist erzeugt, der Versand ist fehlgeschlagen: ${err instanceof Error ? err.message : String(err)} – bitte auf der Abrechnungsseite erneut senden.`,
        );
      }
    } finally {
      setLinkBusy(null);
      paymentsQ.reload();
      activityQ.reload();
    }
  }

  async function transition(to: EventStatus, note?: string, neueFassung?: boolean) {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    setSperre(null);
    try {
      await mitVarianteWahl((chosenOptionId) =>
        apiMutate(`/api/events/${id}/transition`, "POST", { to, ...(note?.trim() ? { note: note.trim() } : {}), ...(chosenOptionId ? { chosenOptionId } : {}), ...(neueFassung ? { neueFassung: true } : {}) }),
      );
      eventQ.reload();
    } catch (err) {
      aktionFehler(err);
      // Der Vorgang steht inzwischen auf einem anderen Status (Gast hat angenommen, anderes Fenster, Mail-Antwort):
      // der Server nennt den Stand im Satz - die Seite lädt selbst neu, damit der Betreiber nicht raten muss.
      if ((err as { code?: string }).code === "invalid_transition") {
        eventQ.reload();
        offersQ.reload();
        activityQ.reload();
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * WARTESCHLANGE PUNKT 47 — eine versehentliche Annahme zurücknehmen.
   *
   * Die Fehlermeldung des Servers wird WÖRTLICH übernommen: er nennt, wie viele Zahlungen,
   * Rechnungen oder Kostenübernahmen im Weg stehen. Antoine, 13.09.2026 — der Betreiber will die
   * konkrete Ursache sehen, nicht „nicht möglich".
   */
  async function annahmeZuruecknehmen() {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      setSperre(null);
      await apiMutate(`/api/events/${id}/annahme-zuruecknehmen`, "POST");
      setAnnahmeZurueckOffen(false);
      setAnnahmeBelege([]);
      eventQ.reload();
    } catch (err) {
      // Hängen Belege dran, bleibt der Dialog offen und zeigt sie einzeln mit ihrem Weg (statt eines Fehlerbalkens,
      // der nur „Belege zuerst stornieren“ sagt).
      const apiErr = err as ApiError;
      const liste = apiErr?.code === "belege_vorhanden" ? (apiErr.body as { stoerendeBelege?: StoerenderBeleg[] } | undefined)?.stoerendeBelege : undefined;
      if (liste && liste.length > 0) {
        setAnnahmeBelege(liste);
      } else {
        aktionFehler(err);
        setAnnahmeZurueckOffen(false);
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Storno-Workflow: Vorgang abbrechen (Owner/Admin) — entwertet offene Zahlungen, zeigt Todos.
   * Reiner API-Aufruf, OHNE Bestätigung davor — die Aufrufer (`handleStorno` unten und die
   * Kostenübernahme-Rückfrage) entscheiden selbst, wie sie den Betreiber vorher fragen.
   */
  async function stornieren(kuEntscheidung?: { kostenuebernahmeEntscheidung: KostenuebernahmeEntscheidung; grund: string }): Promise<string | null> {
    if (!id) return "kein Vorgang geladen";
    setBusy(true);
    try {
      // M-09: Die Todos werden serverseitig in events.details persistiert — eventQ.reload()
      // holt sie, ein lokaler React-State ist nicht mehr noetig (ging bei Reload verloren).
      await apiMutate(`/api/events/${id}/cancel`, "POST", kuEntscheidung ?? {});
      setKuLage(null);
      eventQ.reload();
      paymentsQ.reload();
      return null;
    } catch (err) {
      // Punkt 94: unterschriebene Kostenuebernahme -> der Server verlangt die Entscheidung des
      // Betreibers und liefert die Lage mit; die Rueckfrage oeffnet sich, kein Fehlertext.
      const apiErr = err as ApiError;
      if (apiErr?.code === "kostenuebernahme_entscheidung_fehlt" && !kuEntscheidung) {
        setKuLage((apiErr.body as { lage: KostenuebernahmeLage }).lage);
        return null;
      }
      const msg = err instanceof Error ? err.message : String(err);
      return /forbidden_role/i.test(msg)
        ? "Nur Inhaber dürfen stornieren."
        : /already_cancelled|409/i.test(msg)
          ? "Vorgang ist bereits storniert."
          : msg;
    } finally {
      setBusy(false);
    }
  }

  /**
   * "Vorgang stornieren": echtes Storno mit Rückfrage, in JEDEM Status. Bis zum 26.09.2026 bog eine
   * frühe Anfrage (`inquiry`/`offer_draft`) hier in den Ablehnen-Mail-Dialog ab — der schickte
   * einen Terminvorschlag UND stornierte. Antoine, 26.09.2026: „Das darf auch nicht heißen
   * Absagen, sondern Verschieben.“ Der Terminvorschlag ist jetzt ein eigener Weg
   * („Verschieben …“ im Status-Menü, `VerschiebenDialog`), der NICHT storniert.
   */
  async function handleStorno() {
    if (!event) return;
    if (
      !(await bestaetigen({
        titel: "Vorgang wirklich stornieren?",
        text: "Offene Zahlungen werden entwertet.",
        bestaetigen: "Stornieren",
        gefahr: true,
      }))
    )
      return;
    void stornieren().then((fehler) => {
      if (fehler) setActionError(fehler);
    });
  }

  /** „Verschieben“ nur, solange noch kein Angebot verschickt ist — dort war früher „Abgesagt“. */
  const verschiebenMoeglich = event?.status === "inquiry" || event?.status === "offer_draft";
  const [verschiebenOffen, setVerschiebenOffen] = useState(false);
  const [kuLage, setKuLage] = useState<KostenuebernahmeLage | null>(null);

  /**
   * Terminvorschlag senden: NUR die Mail (send-freetext mit den gewählten Terminen — der Server
   * schreibt daraus „Terminvorschlag gesendet: …“ in den Verlauf). Kein Storno, kein
   * Statuswechsel: der Vorgang bleibt offen, bis der Gast antwortet.
   */
  async function verschiebenSenden(subject: string, text: string, ccSelf: boolean, termine: string[]): Promise<VerschiebenSendeErgebnis> {
    if (!id) return { emailSent: false, emailError: "kein Vorgang geladen" };
    try {
      const data = await apiMutate<{ emailSent: boolean; emailError: string | null }>(`/api/events/${id}/offers/send-freetext`, "POST", {
        subject,
        text,
        ccSelf,
        terminvorschlaege: termine,
      });
      if (data.emailSent) activityQ.reload();
      return { emailSent: data.emailSent, emailError: data.emailError };
    } catch (err) {
      return { emailSent: false, emailError: err instanceof Error ? err.message : String(err) };
    }
  }

  /**
   * Hard-Delete: der Server ist die Wahrheit (Inhaber-Rolle + exakte Vorgangsnummer, sonst
   * 403/422 und KEINE Loeschung) — das Modal ist die scharfe Warnung davor, kein Schutz.
   */
  async function hardDelete() {
    if (!id) return;
    setHardDeleteBusy(true);
    setHardDeleteFehler(null);
    try {
      await apiMutate(`/api/events/${id}/hard-delete`, "POST", { eventNumberConfirm: hardDeleteInput });
      nav(event ? vorgangsListe(event).to : BUCHUNGEN_PFAD);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setHardDeleteFehler(
        /forbidden_role/i.test(msg) ? "Nur der Inhaber darf einen Vorgang endgültig löschen." : msg,
      );
    } finally {
      setHardDeleteBusy(false);
    }
  }

  /** "Erinnerung senden" (Card L1, Anhang E): stellt die AKTUELLE Angebotsversion erneut zu.
   *  H-25: eigene Resend-Route — erzeugt KEINE neue Version und KEINEN neuen Snapshot mehr
   *  (vorher lief das ueber /offers/send und jede Erinnerung wurde eine neue Rechtsversion).
   *
   *  09.09.2026 (Antoine-Auftrag, Screenshot der Veranstaltungsseite): der Knopf sendete bisher
   *  OHNE Vorschau/Editiermoeglichkeit - "Ich will dann wieder das Mailformular in der Mitte
   *  sehen und alles auswählen können." Der eigentliche Versand + das Nachladen der drei Queries
   *  passiert jetzt in `ErinnerungSendenDialog.onSent` weiter unten, dieser Knopf oeffnet nur
   *  noch den Dialog. */
  function onErinnerungGesendet() {
    offersQ.reload();
    eventQ.reload();
    activityQ.reload();
  }

  /**
   * § 8 (docs/KONZEPT-ZAHLUNGSKONDITIONEN-PRO-ANGEBOT.md, Migration 345, Antoine 31.08.2026):
   * "Telefonisch angenommen" — falls der Gast statt online anzunehmen anruft. Loest serverseitig
   * DENSELBEN internen Annahme-Pfad aus wie die oeffentliche Angebotsseite (POST
   * /api/events/:id/telefonisch-angenommen, lib/offer-accept.ts). Bei paymentPathway=
   * 'kostenuebernahme' entsteht dabei bewusst KEIN automatischer Vertrag (H-41) — der Hinweis
   * unten leitet dann zur bestehenden Kostenübernahme-Karte in der Abrechnung weiter.
   */
  function telefonischAngenommen(res: TelefonischAngenommenErgebnis) {
    eventQ.reload();
    offersQ.reload();
    activityQ.reload();
    paymentsQ.reload();
    if (res?.paymentPathway === "kostenuebernahme") {
      void bestaetigen({
        titel: "Angenommen",
        text: "Zahlungsweg „Kostenübernahme“ — bitte jetzt in der Abrechnung den Vertrag anfordern.",
        bestaetigen: "Verstanden",
        abbrechen: false,
      });
    }
  }

  function telefonischFehler(msg: string, err?: unknown) {
    const code = err && typeof err === "object" ? (err as { code?: string }).code : undefined;
    const s = err === undefined ? null : sperreAusFehler(err);
    if (s) {
      setActionError(null);
      setSperre(s);
    } else {
      setActionError(
        /invalid_status/i.test(msg)
          ? "Nur möglich, solange das Angebot versendet und noch nicht angenommen ist."
          : /forbidden_role/i.test(msg)
            ? "Nur Inhaber dürfen ein Angebot telefonisch als angenommen markieren."
            : msg,
      );
    }
    // Der Vorgang hat inzwischen einen anderen Stand (Gast hat angenommen, Status geändert): Seite von selbst neu laden.
    if (code === "invalid_status" || code === "conflict" || code === "invalid_transition") {
      eventQ.reload();
      offersQ.reload();
      activityQ.reload();
    }
  }

  /**
   * WP1 (Auftrags-Dashboard, 08.09.2026): ersetzt den bisherigen Aktivität-Feed (E-Mail-Karten +
   * Status-Zeilen, siehe git-history) sowie Versionsarchiv/Vorgangsansicht durch EINE Ableitung
   * aus drei bestehenden Quellen — reine Funktion, getestet in
   * `apps/web/test/auftrag-verlauf.test.ts`. Siehe `lib/auftrag-verlauf.ts` fuer die drei
   * verbindlichen Regeln aus Antoines Screenshot-Beleg (immer Datum+Uhrzeit, Mail+Status derselben
   * Version zu einer Zeile, Burst-Zusammenfassung binnen 60s).
   */
  /** `offer_history[].sentAt` der FRÜHESTEN gesendeten Version, `null` wenn noch nie gesendet —
   *  Grenze der Erfassungsphase (PR4, `lib/feld-versionen.ts` Regel B), geteilt zwischen der
   *  Verlaufs-Sammelzeile unten und dem `FeldVersionenDrawer`. */
  const ersteAngebotsversionGesendetAm = useMemo(() => {
    const gesendet = (historyQ.data ?? [])
      .map((h) => h.sentAt)
      .filter((s): s is string => Boolean(s))
      .sort();
    return gesendet[0] ?? null;
  }, [historyQ.data]);

  const verlaufEintraege = useMemo(() => {
    const basis = leiteAuftragsVerlaufAb({
      activity: activityQ.data ?? [],
      history: historyQ.data ?? [],
      emails: emailsQ.data ?? [],
      payments: paymentsQ.data ?? [],
    });
    // PR4 Regel C: statt einzelner Feldzeilen fuer Eckdaten-Aenderungen NUR EINE verdichtete
    // Sammelzeile pro Buendel (die Erfassungsphase erzeugt hier laut Regel B/C bewusst NULL
    // Zeilen). `key` traegt ein eigenes Praefix, damit sie nie mit einer `activity-<id>`-Zeile
    // kollidiert.
    const eckdatenZeilen = buendleFuerVerlaufskarte(feldVersionenQ.data ?? [], ersteAngebotsversionGesendetAm).map(
      (bundle) => ({
        key: `eckdaten-${bundle.key}`,
        art: "eckdaten" as const,
        zeitpunkt: bundle.zeitpunkt,
        titel: `Eckdaten geändert · ${bundle.felder.map(feldLabel).join(", ")}`,
        detail: `durch ${bundle.actorLabel ?? (bundle.actorType === "customer" ? "Kunde" : "System")}`,
        icon: "edit",
      }),
    );
    return [...basis, ...eckdatenZeilen].sort((a, b) => new Date(b.zeitpunkt).getTime() - new Date(a.zeitpunkt).getTime());
  }, [activityQ.data, historyQ.data, emailsQ.data, paymentsQ.data, feldVersionenQ.data, ersteAngebotsversionGesendetAm]);

  /**
   * F1 (`docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md` Teil B2): ein Vorgang, der aus einer
   * Anfrage stammt, aber noch KEIN Angebot hat, konnte hier bisher nur "Noch kein Angebot" sagen
   * — der Weg zum Angebot (KI oder manuell) lag ausschliesslich auf `pages/anfrage-detail.tsx`.
   * Seit Antoines Entscheidung D3 (ein Fall, eine Adresse) landet der Betreiber aber genau hier.
   * Dieselben zwei Aktionen, derselbe Hook wie dort (`hooks/useAngebotUebernahme.ts`) — mit dem
   * EINEN Unterschied, dass nach dem Erfolg nicht navigiert, sondern nur neu geladen wird: der
   * Server befuellt den BESTEHENDEN Vorgang (`ladeBestehendenVorgang` in
   * `apps/api/src/routes/inquiries.ts`), `res.eventId` ist also dieser Vorgang selbst.
   */
  const [uebernahmeKundeId, setUebernahmeKundeId] = useState<string | null>(null);
  const uebernahme = useAngebotUebernahme(event?.inquiryId ?? null, {
    onErfolg: () => {
      eventQ.reload();
      offersQ.reload();
      activityQ.reload();
    },
    uebernahmeKundeId,
    onReload: () => eventQ.reload(),
  });

  if (eventQ.loading) return <LoadingRows rows={6} />;
  if (eventQ.error) return <ErrorNote message={eventQ.error} />;
  if (!event) return <EmptyState icon="event_busy" title="Event nicht gefunden" />;

  const stepIdx = STEPPER.indexOf(event.status);
  const customer = customerQ.data;
  // Hard-Delete: die ANGEZEIGTE Vorgangsnummer — genau gegen diese Anzeige prueft der Server die
  // Bestaetigung (events.number ist nullable). Bewusst NICHT auf den Kundennamen (A1) umgestellt:
  // hier geht es um die technische Kennung, nicht um eine Ueberschrift.
  const anzeigeNummer = event.number ?? event.id.slice(0, 8);
  /**
   * A1 (Antoine, 13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md` Einheit A): im Kopf stand bisher
   * die Vorgangsnummer bzw. — wenn keine vergeben ist — die ersten acht Zeichen der UUID
   * („40C76147"). Das ist fuer einen Menschen keine Auskunft. Stattdessen **Firma bevorzugt,
   * sonst Personenname** — dieselbe Reihenfolge wie an den bereits bestehenden Stellen weiter
   * unten (`FirmenprofilKarte`, `AuftragGespraechKarte`). Erst wenn BEIDES fehlt, faellt
   * die Anzeige auf die bisherige Nummer/Kurz-ID zurueck — nie ein leerer Kopf.
   * `customer` kommt aus dem ohnehin geladenen `/api/customers/:id`; solange das noch laeuft,
   * greift `event.customerName` (liefert `GET /api/events/:id` mit).
   */
  const kundenBezeichnung = customer?.company?.trim() || customer?.name?.trim() || event.customerName?.trim() || null;
  const kopfBezeichnung = kundenBezeichnung ?? anzeigeNummer;
  // A6/E7 (13.09.2026): steuert die Gewichtung des Küchenzettel-Knopfs in der Aktionsreihe —
  // Begründung am Knopf selbst bzw. in `ui/format.ts::istVeranstaltungstag`.
  const kuechenzettelHeuteNoetig = istVeranstaltungstag(event.eventDate, event.dateEnd ?? null);
  // Preis-sortiert wie `builder.tsx` (`options`) - keine Buchstaben-Kennung mehr (Antoine,
  // 11.09.2026): Titel oder Preis identifizieren die Variante.
  const offers = aLaCarteAnsEnde(sortiereVariantenNachPreis((offersQ.data ?? []).filter((o) => o.isActive)));
  /**
   * F1/F2 (`docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md` Teil B2, Inventar "nichts geht
   * verloren"): Zeigt die Angebots-Karte statt des reinen Leerzustands die Uebernahme-Aktionen?
   * Zwei Bedingungen, beide noetig: (1) der Vorgang stammt aus einer Anfrage — nur dann gibt es
   * ein `/api/inquiries/:id/komponieren`-/`manuell-uebernehmen`-Ziel; (2) es gibt noch kein
   * Angebot. Der Dubletten-Hinweis haengt bewusst an DERSELBEN Bedingung: seine Auswahl wirkt
   * ausschliesslich ueber die Uebernahme-Knoepfe (sie geht als `customerId` mit), ohne sie waere
   * er ein Vorschlag ohne Wirkung.
   *
   * `zeigtKiUebernahme` ist ENGER (nur im Entwurfs-Stand): auf der alten `anfrage-detail.tsx`
   * war der KI-Weg an `canGenerate` (Status "new"/"quoted") gebunden, der manuelle Weg dagegen
   * IMMER erreichbar, "auch bei Status Abgesagt — z. B. um eine verlorene Anfrage doch noch
   * anzubieten" (Antoine, 02.09.2026, wörtlich aus dem Kommentar der geloeschten Seite) — "ohne
   * Gaestezahl. Das kann ja nachgeholt werden. Darf aber nicht alles blockieren." Deshalb bleibt
   * "Manuell erstellen" hier zusaetzlich bei `cancelled` verfuegbar (ein Vorgang kann direkt aus
   * `offer_draft` heraus storniert werden, ohne je ein Angebot gehabt zu haben), waehrend
   * "KI-Angebot erzeugen" wie zuvor nur im Entwurfs-Stand erscheint.
   */
  const zeigtUebernahme =
    Boolean(event.inquiryId) && offers.length === 0 && (event.status === "offer_draft" || event.status === "cancelled");
  const zeigtKiUebernahme = zeigtUebernahme && event.status === "offer_draft";
  // WP3a (Auftrags-Dashboard, 08.09.2026): Ziel-Variante fuer "Auch im Angebot uebernehmen" in
  // `AuftragEckdatenKarte` — die verbindlich gewaehlte, sonst die guenstigste.
  const zielAngebotQuelle = offers.find((o) => o.isChosen) ?? offers[0] ?? null;
  const zielAngebot = zielAngebotQuelle
    ? { id: zielAngebotQuelle.id, guestCount: zielAngebotQuelle.guestCount, label: zielAngebotQuelle.title || money(zielAngebotQuelle.amountTotalCents) }
    : null;
  const payments = paymentsQ.data ?? [];
  // WP2 (Auftrags-Dashboard-Kennzahlen, 08.09.2026): Bezahlt/Offen/Nächste-Fälligkeit-Berechnung
  // inkl. der "vollständig bezahlt"-Sonderregel (M1, Bughunt 2026-07-11) zog vorher hier UND in
  // `pages/abrechnung.tsx` fast identischen Code — jetzt eine Stelle, `lib/zahlungs-kennzahlen.ts`.
  // Abrechnung Stufe 3: mit abgeschlossener Abrechnung misst „Bezahlt/Offen“ an ihrem Betrag.
  const kz = kennzahlenFuerVorgang(payments, event);
  const { paidCents, openCents, naechsteFaelligkeit } = kz;
  // Zahlungsstand (Mockup 19.10.2026): EIN Abzeichen im Kopf + Schritt „Bezahlt“ halb/grün.
  const zahlungsMeta = zahlungsstandMeta(event, payments);
  const schrittZahlungArt = zahlungsSchrittArt(zahlungsstand(event, payments));
  // Zahlweg-Hinweis (Mimmo, 02.11.2026): WIE der Rest bezahlt wird, neben „Anzahlung bezahlt“. Der Weg
  // kommt aufgelöst vom Server (`event.zahlweg`); erst ab der Zusage, nie bei Absage/Erstattung.
  const zahlwegStand = zahlungsstand(event, payments);
  const zahlwegHinweis = ["offer_chosen", "balance_due", "payment_failed", "paid", "completed"].includes(event.status)
    ? zahlwegText(event.zahlweg, { bezahltCents: zahlwegStand?.bezahltCents ?? paidCents, gesamtCents: zahlwegStand?.gesamtCents ?? event.amountTotalCents })
    : null;
  // Z0.3 (Befund B-3): Deckt der PLAN die Angebotssumme? Das ist eine andere Frage als „wie viel
  // ist eingegangen" — ein Plan ueber die halbe Summe sah bisher aus wie ein vollstaendiger.
  // Bei Deckungsgleichheit liefert die Formulierung bewusst `null`: kein Hinweis ohne Anlass.
  // Abrechnung Stufe 3: nach einer abgeschlossenen Abrechnung ist der Zahlungsplan kein Maßstab mehr —
  // „Plan fordert X von Angebotssumme Y“ wäre dann falsch. Maßgeblich ist der Rest der Abrechnung.
  const deckungshinweis = event.abrechnungDeckung ? null : deckungsSatz(paymentsQ.zusatz?.deckung);
  // Punkt 29 P2: nach einer geänderten und erneut angenommenen Fassung. Steht er da, ersetzt er den
  // allgemeinen Deckungshinweis — beide sagten dasselbe, nur einer führt weiter.
  const planHinweis = zahlungsplanHinweisNachAenderung({
    status: event.status,
    aktivitaeten: activityQ.data ?? [],
    amountTotalCents: event.amountTotalCents,
    payments,
  });
  const ohneLink = ratenOhneGueltigenLink(payments, activityQ.data ?? []);
  // Punkt 38 (Antoine, 23.09.2026): nach einer Erstattung NICHT automatisch aus 'paid' zurueck —
  // der Betreiber wird gefragt. Der Knopf nutzt den Uebergang paid -> balance_due bzw. -> refunded
  // (lib/event-status.ts, Owner/Admin-Gate im Server).
  const rueckfallHinweis = erstattungsRueckfallHinweis(event.status, paidCents, kz.sollCents);
  // Punkt 93: volle Erstattung -> „Erstattet“ statt „Zahlung fehlgeschlagen“.
  const rueckfallZiel = erstattungsRueckfallZiel(paidCents);
  const ortChip = ortTypChip(event.locationType, event.offSiteMode);
  const isPast = event.eventDate ? new Date(event.eventDate) < new Date(new Date().toDateString()) : false;
  const offerSentCount = (activityQ.data ?? []).filter((a) => a.action === "offer.sent").length;
  // Antoine (11.09.2026, Screenshot): "Erinnerung verschickt" muss neben "Angebot gesendet"
  // sichtbar sein, nicht in derselben Zahl verschwinden - offer.resent (Erinnerung-senden-Dialog,
  // routes/offers.ts POST .../offers/resend) ist im Aktivitaetsprotokoll bereits ein eigener
  // Aktionscode, wurde hier bisher nur nicht ausgelesen.
  const offerResentCount = (activityQ.data ?? []).filter((a) => a.action === "offer.resent").length;
  /*
   * Punkt 84, zweite Haelfte (20.09.2026): DAS „ZULETZT"-DATUM WAR IRREFUEHREND. `event.offerSentAt`
   * wird vom Erinnerungsversand UEBERSCHRIEBEN — dort stand „Angebot gesendet, zuletzt TT.MM."
   * und gemeint war die letzte ERINNERUNG. Die Trennung liegt in `lib/versand-zeitpunkte.ts`,
   * samt Zusicherungen: dort steht auch, dass das Protokoll absteigend kommt (der erste Treffer
   * ist der juengste) und warum der Rueckfall auf das alte Feld fuer Altvorgaenge bleiben muss.
   */
  const { letzterVersandAm, letzteErinnerungAm } = versandZeitpunkte(activityQ.data ?? [], event.offerSentAt ?? null);
  // F4 (docs/KONZEPT-ABSAGE-STORNO-LEBENSZYKLUS.md, Entscheidung 4): "Storno im Hintergrund" heisst
  // ein sichtbarer Hinweis bei einer Absage MIT bereits bezahlten Zahlungen — kein automatischer
  // Geldfluss. `paidCents` ist bereits die Summe aller `status === "succeeded"`-Zahlungen
  // (lib/zahlungs-kennzahlen.ts). Nur fuer `cancelled` UND paidCents > 0 relevant — sonst bleibt
  // die Seite wie heute (kein Hinweis ohne Anlass).
  const zeigeStornoHinweis = event.status === "cancelled" && paidCents > 0;
  const stornoTage = zeigeStornoHinweis ? tageBisEvent(event.eventDate, event.dateEnd ?? null) : null;
  const stornoKeepPercent =
    zeigeStornoHinweis && stornoTage != null ? ermittleKeepPercent(settingsQ.data?.cancellationTiers, stornoTage) : null;

  // ── Anzeige „Salon“ (Antoine, 26.09.2026) ────────────────────────────────────────────────────
  /**
   * Status „Entwurf“: im Mockup orange getönt — abgestimmt auf den Kalender, der Entwurf seit dem
   * 26.09.2026 rot zeigt (`kalenderTon` in `pages/calendar.tsx`: „Anfrage da, Angebot noch nicht
   * gesendet“ = nur angefragt). Orange ist auf dieser Seite der Handlungsfarbe vorbehalten (der
   * EINE Knopf je Status); dieselbe Farbe für einen Zustand hätte den Knopf entwertet.
   */
  const kopfStatus: StatusMeta = EVENT_STATUS[event.status];
  const tage = daysUntil(event.eventDate);
  // Punkt 130: nur ein Monat bekannt (Widget-Angabe oder Anfragetext) → Kalenderblatt + Checkliste.
  const wunschMonat = wunschMonatVon({
    eventDate: event.eventDate,
    angaben: event.anfrageAngaben,
    text: event.customerNotes,
    eingangAm: event.sourceNote?.receivedAt ?? event.createdAt,
  });
  const bald = tage === "heute" || tage === "morgen" || /^in [2-7] Tagen$/.test(tage ?? "");
  const terminHinweis: StatusMeta | null =
    isPast && !["completed", "cancelled", "refunded"].includes(event.status)
      ? { label: "Termin liegt in der Vergangenheit", tone: "warn" }
      : tage && !isPast
        ? { label: tage, tone: bald ? "warn" : "neutral" }
        : null;
  const kundeZusatz =
    [
      customer?.company?.trim() ? customer.name : null,
      `${event.inquiryId ? "Anfrage vom" : "angelegt am"} ${formatDate(event.createdAt)}`,
      // Reisegruppen-Widget T5: Kurzkennung der Anfrage (der Stamm-Vorgang hat keine eigene Nummer).
      event.inquiryId && event.anfrageAngaben ? anfrageNummer(event.inquiryId) : null,
    ]
      .filter(Boolean)
      .join(" · ") || null;
  /** Wie der Gast im Satz heißt: Firma, sonst Vorname. */
  // Regel keine-sackgassen (04.11.2026), Punkt 4: eine abgelaufene Gültigkeit sperrt später still (Erinnerung, Zusage) - darum sofort sichtbar.
  const ablaufSeit = event.status === "offer_sent" && gueltigkeitAbgelaufen(event.offerValidUntil) ? (event.offerValidUntil as string) : null;
  const ablaufHinweis = ablaufSeit ? (
    <span className="mt-1 block text-secondary-fixed-dim" data-testid="gueltigkeit-abgelaufen-hinweis">
      Angebot seit {gueltigDatumDe(ablaufSeit)} abgelaufen.
    </span>
  ) : null;
  const ablaufKnopf = ablaufSeit ? (
    <Button type="button" variant="secondary" className="!py-2" onClick={() => { setActionError(null); setGueltigkeitOffen(true); }} data-testid="gueltigkeit-verlaengern">
      <Icon name="event_repeat" className="text-[16px]" /> Gültigkeit verlängern
    </Button>
  ) : null;
  const anrede = customer?.company?.trim() || customer?.name?.trim().split(/\s+/)[0] || event.customerName?.trim().split(/\s+/)[0] || "den Kunden";
  // Ort: Freitext + Ort-Typ; fehlt etwas, führt der Klick in den Drawer „Eckdaten“ (A2/WP2).
  const ortFehlt = !event.location && (ortChip.fehlt || ortFehltHinweis(event.location, event.locationType));
  const ortHinweis = ortFehltHinweis(event.location, event.locationType);
  // Antoine 28.09.2026: „Wenn der Ort steht, muss nicht stehen ‚· Außer Haus · Catering‘“ — die
  // Art steht im Klein-Fenster der Kachel.
  const ortText = event.location
    ? event.location
    : ortChip.fehlt
      ? ortChip.label
      : ortHinweis
        ? `${ortChip.label} · Ort ergänzen`
        : ortChip.label;
  const uhrzeit = uhrzeitText(event.timeFrom, event.timeTo);
  const angebotQuelle = offers.find((o) => o.isChosen) ?? (offers.length === 1 ? offers[0] : null);
  /**
   * Punkt (c), 27.09.2026 (Antoine-Korrektur): „noch kein Angebot“ vs. „Angebot ohne Positionen“
   * vs. fertiges Angebot — dieselbe Dreiteilung wie das Board (`packages/anfrage-stand`s
   * `hatPositionen`, aus `apps/api/src/lib/anfragen-abfrage.ts`s `EXISTS`-Prüfung auf
   * `offer_items`). Referenzfall: Vorgang 5db7d277… hat eine Option A OHNE Positionen —
   * `event.amountTotalCents` blieb dort `null` (der Sync auf `events.amount_total_cents`,
   * `syncEventAmountTotal` in `routes/offer-items.ts`, läuft erst beim Speichern von Positionen),
   * das alte „noch kein Angebot“ war also schlicht falsch: es GIBT eine Option.
   *
   * Bughunt 12.10.2026: `GET /api/events/:id/offers` liefert je Variante `positionCount` (echte
   * Zählung auf `offer_items`). Der frühere Ersatz `subtotalCents > 0` sagte bei Positionen ohne
   * Einzelpreis fälschlich „Angebot ohne Positionen“. `subtotalCents` bleibt nur Rückfall, falls
   * eine ältere API-Antwort das Feld noch nicht trägt.
   */
  const hatAngebotOptionen = offers.length > 0;
  const hatPositionen = offers.some((o) => (o.positionCount != null ? o.positionCount > 0 : o.subtotalCents > 0));
  /**
   * Angebotswert: `events.amount_total_cents` ist autoritativ. Ist er (noch) leer, gilt die
   * server-gerechnete Summe der maßgeblichen Variante (gewählt bzw. einzige) — dieselbe Zahl,
   * die `syncEventAmountTotal` (routes/offer-items.ts) dorthin spiegelt. Kein Client-Rechnen.
   */
  const angebotswertCents = event.amountTotalCents ?? (hatPositionen ? angebotQuelle?.amountTotalCents ?? null : null);
  /**
   * „À la carte“ (Mimmo, 04.11.2026): „Angebotswert 0,00 € · 0,00 € pro Gast“ stand da, obwohl es bewusst keinen Betrag
   * gibt. Gewählte/einzige Variante à la carte (oder alle aktiven) und kein Betrag: Kopfzeile und Kennzahlenleiste
   * sagen „à la carte · Abrechnung vor Ort“, und es gibt keine Positionen, die man vermissen müsste.
   */
  const alaCarteAngebot =
    offers.length > 0 && (angebotQuelle ? istALaCarteOption(angebotQuelle) : offers.every((o) => istALaCarteOption(o))) && (angebotswertCents ?? 0) <= 0;
  const uhrzeitBearbeiten = (schliessen: () => void) => (
    <UhrzeitFenster von={event.timeFrom} bis={event.timeTo} speichern={vorgangPatch} schliessen={schliessen} />
  );
  const gaesteBearbeiten = (schliessen: () => void) => <GaesteFenster gaeste={event.guestCount} speichern={vorgangPatch} schliessen={schliessen} />;
  const ortBearbeiten = (schliessen: () => void) => (
    <OrtFenster ort={event.location} locationType={event.locationType ?? null} offSiteMode={event.offSiteMode ?? null} speichern={vorgangPatch} schliessen={schliessen} />
  );
  // Handy-Raster (Mockup 16.10.2026): Beginn|Gäste, Ort|Art, Angebot über volle Breite. Ohne Art
  // (nichts Belastbares) keine leere Kachel — dann geht der Ort über volle Breite.
  const art = essensArt(artPositionenQ.data?.items, artPaketeQ.data, angebotQuelle?.title);
  const fakten: VorgangFakt[] = [
    { icon: "schedule", ton: "primary", label: "Beginn", wert: uhrzeit ?? "Uhrzeit offen", leer: !uhrzeit, testId: "kopf-beginn", bearbeiten: uhrzeitBearbeiten },
    {
      icon: "group",
      ton: "info",
      label: "Gäste",
      wert: event.guestCount != null ? `${event.guestCount} ${event.guestCount === 1 ? "Person" : "Personen"}` : "Gäste offen",
      leer: event.guestCount == null,
      testId: "kopf-gaeste",
      bearbeiten: gaesteBearbeiten,
    },
    { icon: "storefront", ton: ortFehlt ? "warn" : "success", label: "Ort", wert: ortText, leer: ortFehlt, breit: !art, bearbeiten: ortBearbeiten, testId: ortHinweis ? "kopf-ort-ergaenzen" : "ort-typ-chip" },
    // Am Computer bleibt der Kopf wie bisher (Mockup: „Am Computer bleibt alles wie heute“).
    ...(art ? [{ icon: "room_service", ton: "success" as const, label: "Art", wert: art, nurHandy: true, testId: "kopf-art" }] : []),
    {
      icon: "restaurant",
      ton: "warn",
      label: "Angebot",
      wert: !hatAngebotOptionen
        ? "noch kein Angebot"
        : alaCarteAngebot
          ? [angebotQuelle?.title || (offers.length > 1 ? `${offers.length} Varianten` : null), A_LA_CARTE_ABRECHNUNG_VOR_ORT].filter(Boolean).join(" · ")
          : !hatPositionen
            ? "Angebot ohne Positionen"
            : [angebotQuelle?.title || (offers.length > 1 ? `${offers.length} Varianten` : null), money(angebotswertCents)].filter(Boolean).join(" · "),
      leer: !hatAngebotOptionen || (!hatPositionen && !alaCarteAngebot),
      breit: true,
      // Antoine 28.09.2026: „oben einen Link zum Angebot“.
      onClick: () => nav(angebotPfad(event.id)),
      testId: "kopf-angebot",
    },
  ];
  const angebotZeile =
    alaCarteAngebot && angebotQuelle
      ? [angebotQuelle.title, angebotQuelle.guestCount > 0 ? `${angebotQuelle.guestCount} Gäste` : null].filter(Boolean).join(" · ") || null
      : angebotQuelle && angebotQuelle.guestCount > 0
      ? [angebotQuelle.title, `${angebotQuelle.guestCount} Gäste`, `${money(Math.round(angebotQuelle.amountTotalCents / angebotQuelle.guestCount))} pro Gast`]
          .filter(Boolean)
          .join(" · ")
      : offers.length > 1
        ? `${offers.length} Varianten zur Auswahl`
        : null;

  /** E6: Am Veranstaltungstag (und bei „Bezahlt“) steht der Küchenzettel in der Aktionskarte. */
  const kuechenzettelInKarte = kuechenzettelHeuteNoetig || event.status === "paid";
  /** „Vorgang stornieren“ im Menü nur, wo „Abgesagt“ nicht ohnehin in der Statusliste steht. */
  const stornoImMenue = event.status !== "cancelled" && (verschiebenMoeglich || !ALLOWED[event.status].includes("cancelled"));
  const kuechenzettelKnopf = (primaer: boolean) => (
    <Button
      type="button"
      variant={primaer ? "primary" : "secondary"}
      className={primaer ? "!px-5 !py-3 !text-[13.5px]" : "!py-2"}
      disabled={printBusy}
      data-testid="kuechenzettel-knopf"
      title="Küchenzettel drucken (Positionen der gewählten Variante, ohne Preise)"
      onClick={() => void printKitchenSheet()}
    >
      <Icon name="print" className="text-[18px]" /> Küchenzettel drucken
    </Button>
  );
  const durchgefuehrtKnopf = (primaer: boolean) => (
    <Button
      type="button"
      variant={primaer ? "primary" : "secondary"}
      className={primaer ? "!px-5 !py-3 !text-[13.5px]" : "!py-2"}
      disabled={busy}
      onClick={() => void transition("completed")}
    >
      <Icon name="task_alt" className="text-[18px]" /> Als durchgeführt markieren
    </Button>
  );
  /**
   * Die Aktionskarte je Status — genau EIN oranger Knopf (Tabelle „Hauptaktion je Status“ im
   * Mockup). Nebenaktionen stehen leise daneben, der Rest im Menü „⋯“. Kein Weg entfällt: was
   * früher im Banner „Nächster Schritt“ oder im Kopf stand, steht jetzt hier oder im Menü.
   */
  const aktion: Parameters<typeof AktionsKarte>[0] | null = (() => {
    if (event.status === "completed") return null;
    // Veranstaltungstag: die Küche braucht den Zettel (A6/E7) — vor jeder anderen Regel.
    if (kuechenzettelHeuteNoetig && ["offer_chosen", "paid", "payment_failed", "balance_due"].includes(event.status)) {
      return {
        icon: "restaurant",
        titel: `Heute ist ${titelAnzeige(event.occasion ?? "die Veranstaltung")}`,
        zeile: [uhrzeit, event.guestCount != null ? `${event.guestCount} Gäste` : null].filter(Boolean).join(" · ") || undefined,
        primaer: kuechenzettelKnopf(true),
        leise: (
          <>
            {ALLOWED[event.status].includes("completed") && durchgefuehrtKnopf(false)}
            {event.status !== "paid" && (
              <ButtonRouterLink to={zurAbrechnungPfad(event)} variant="secondary" className="!py-2">
                <Icon name="receipt_long" className="text-[16px]" /> Zur Abrechnung
              </ButtonRouterLink>
            )}
            {event.status === "offer_chosen" && (
              <Button type="button" variant="ghost" className="!py-2" disabled={busy} onClick={() => setAnnahmeZurueckOffen(true)}>
                <Icon name="undo" className="text-[16px]" /> Annahme zurücknehmen
              </Button>
            )}
          </>
        ),
        testId: "aktionskarte",
      };
    }
    switch (event.status) {
      case "inquiry":
      case "offer_draft":
        return {
          icon: "edit_document",
          titel: offers.length > 0 ? `Angebot fertigstellen und an ${anrede} senden` : `Angebot erstellen und an ${anrede} senden`,
          checkliste: [
            {
              text: event.eventDate
                ? `Datum ${formatDate(event.eventDate)}`
                : wunschMonat
                  ? `Tag festlegen (${monatKurzText(wunschMonat)})`
                  : "Datum eintragen",
              erledigt: Boolean(event.eventDate),
            },
            { text: event.guestCount != null ? `${event.guestCount} Gäste` : "Gästezahl eintragen", erledigt: event.guestCount != null },
            {
              // Punkt (c), 27.09.2026: dieselbe Dreiteilung wie das Faktenkärtchen oben — ein
              // Haken bei einer leeren Variante (Vorgang 5db7d277…, Option A ohne Positionen)
              // behauptete bisher "Angebot angelegt", war aber inhaltlich noch nicht fertig.
              text: !hatAngebotOptionen
                ? "Angebot anlegen"
                : !hatPositionen
                  ? "Angebot ohne Positionen"
                  : offers.length === 1
                    ? "Angebot angelegt"
                    : `${offers.length} Varianten angelegt`,
              erledigt: hatAngebotOptionen && hatPositionen,
            },
          ],
          // CX-Audit 2026-07-17: der empfohlene Schritt führt direkt zum Ort der Handlung.
          primaer: (
            <ButtonRouterLink to={angebotPfad(event.id)} variant="primary" className="!px-5 !py-3 !text-[13.5px]">
              <Icon name="arrow_forward" className="text-[18px]" /> {offers.length > 0 ? "Angebot fertigstellen" : "Angebot erstellen"}
            </ButtonRouterLink>
          ),
          testId: "aktionskarte",
        };
      case "offer_sent":
        // sql/770 („Alte Fassung kurz sperren“): nach dem Umschalten von „À la carte“ ist die versendete Fassung
        // gesperrt, bis neu gesendet wird - Erinnerung und telefonische Annahme lehnt der Server dann ab.
        if (event.offerFassungVeraltetSeit) {
          return {
            icon: "edit_document",
            titel: `${anrede} hat noch nicht gewählt`,
            zeile: (
              <>
                <span data-testid="fassung-neu-senden">{FASSUNG_NEU_SENDEN}</span>
                {ablaufHinweis}
              </>
            ),
            primaer: (
              <ButtonRouterLink to={angebotSendenPfad(event.id)} variant="primary" className="!px-5 !py-3 !text-[13.5px]" data-testid="fassung-jetzt-neu-senden">
                <Icon name="send" className="text-[18px]" /> Jetzt neu senden
              </ButtonRouterLink>
            ),
            leise: (
              <>
                {ablaufKnopf}
                <Button type="button" variant="secondary" className="!py-2" onClick={() => { setActionError(null); setSperre(null); setTelefonischOffen(true); }} data-testid="telefonisch-mit-neuer-fassung">
                  <Icon name="call" className="text-[16px]" /> Telefonisch angenommen
                </Button>
                <Button type="button" variant="secondary" className="!py-2" onClick={() => { setDeclineWahl(""); setDeclineFrei(""); setDeclineOpen(true); }}>
                  <Icon name="not_interested" className="text-[16px]" /> Kunde hat abgesagt
                </Button>
              </>
            ),
            testId: "aktionskarte",
          };
        }
        return {
          icon: "forward_to_inbox",
          titel: `${anrede} hat noch nicht gewählt`,
          zeile: (
            <>
            {/* Punkt 84: die Versandzeile ist ein Knopf und öffnet die vollständige Liste aller
               Versände und Erinnerungen (dieselbe wie im Kopf der Verlaufs-Karte). */}
            <button
              type="button"
              onClick={() => setVersandVerlaufOffen(true)}
              aria-label="Vollständige Liste aller Versände und Erinnerungen öffnen"
              data-testid="versandzeile-verlauf-oeffnen"
              className="text-left underline decoration-dotted underline-offset-2 hover:text-on-background hover:decoration-solid dark:hover:text-inverse-on-surface"
            >
              Angebot {offerSentCount > 0 ? `${offerSentCount}× ` : ""}gesendet
              {letzterVersandAm ? `, zuletzt ${formatDateTime(letzterVersandAm)}` : ""}
              {offerResentCount > 0
                ? `, zusätzlich ${offerResentCount}× als Erinnerung verschickt${letzteErinnerungAm ? `, zuletzt ${formatDateTime(letzteErinnerungAm)}` : ""}`
                : ""}{" "}
              — noch keine Auswahl getroffen.
            </button>
            {ablaufHinweis}
            </>
          ),
          primaer: (
            <Button type="button" className="!px-5 !py-3 !text-[13.5px]" onClick={() => setReminderOpen(true)}>
              <Icon name="send" className="text-[18px]" /> Erinnerung senden
            </Button>
          ),
          leise: (
            <>
              {ablaufKnopf}
              {/* § 8 (Migration 345): der Betreiber-Ersatz für den Kunden-Klick, wenn der Gast anruft. */}
              <Button type="button" variant="secondary" className="!py-2" onClick={() => { setActionError(null); setSperre(null); setTelefonischOffen(true); }}>
                <Icon name="call" className="text-[16px]" /> Telefonisch angenommen
              </Button>
              {/* Antoine 01.09.2026: Gegenstück — reines internes Vermerken, darum neutral. */}
              <Button type="button" variant="secondary" className="!py-2" onClick={() => { setDeclineWahl(""); setDeclineFrei(""); setDeclineOpen(true); }}>
                <Icon name="not_interested" className="text-[16px]" /> Kunde hat abgesagt
              </Button>
            </>
          ),
          testId: "aktionskarte",
        };
      case "offer_declined":
      case "no_response":
        return {
          icon: "restart_alt",
          titel: NEXT_STEP_COPY[event.status] ?? "Status prüfen",
          zeile: "Den Vorgang schließen oder den Status ändern: Menü „⋯“ oben.",
          primaer: (
            <ButtonRouterLink to={angebotPfad(event.id)} variant="primary" className="!px-5 !py-3 !text-[13.5px]">
              <Icon name="edit_document" className="text-[18px]" /> Neues Angebot erstellen
            </ButtonRouterLink>
          ),
          testId: "aktionskarte",
        };
      case "offer_chosen":
      case "payment_failed":
      case "balance_due":
        return {
          icon: "receipt_long",
          titel:
            event.status === "offer_chosen"
              ? `${anrede} hat zugesagt: ${lexofficeKeyFehlt ? "Zahlung einrichten" : "Rechnung und Zahlung einrichten"}`
              : event.status === "balance_due"
                ? "Restforderung offen: Restbetrag anfordern"
                : "Zahlung klären",
          primaer: (
            <ButtonRouterLink to={zurAbrechnungPfad(event)} variant="primary" className="!px-5 !py-3 !text-[13.5px]">
              <Icon name="receipt_long" className="text-[18px]" /> Zur Abrechnung
            </ButtonRouterLink>
          ),
          /* WARTESCHLANGE PUNKT 47 — der Weg zurück; der Server lehnt ab, sobald Belege hängen. */
          leise:
            event.status === "offer_chosen" ? (
              <Button type="button" variant="secondary" className="!py-2" disabled={busy} onClick={() => setAnnahmeZurueckOffen(true)}>
                <Icon name="undo" className="text-[16px]" /> Annahme zurücknehmen
              </Button>
            ) : undefined,
          testId: "aktionskarte",
        };
      case "paid":
        return {
          icon: "task_alt",
          titel: "Durchführung vorbereiten",
          zeile: [uhrzeit, event.guestCount != null ? `${event.guestCount} Gäste` : null, tage].filter(Boolean).join(" · ") || undefined,
          primaer: durchgefuehrtKnopf(true),
          leise: kuechenzettelKnopf(false),
          testId: "aktionskarte",
        };
      case "cancelled":
        // F4: Absage MIT bezahlten Zahlungen — sichtbarer Hinweis statt automatischem Geldfluss.
        if (zeigeStornoHinweis)
          return {
            ton: "gefahr",
            icon: "receipt_long",
            titel: `Diese Absage hat ${money(paidCents)} bezahlte Zahlungen — Entscheidung nötig`,
            zeile: `Kein automatischer Geldfluss: du entscheidest, ob und wie viel zurückerstattet wird.${
              stornoKeepPercent != null
                ? ` Laut Stornobedingungen (${stornoTage} ${stornoTage === 1 ? "Tag" : "Tage"} bis zum Termin) werden ${stornoKeepPercent} % einbehalten.`
                : ""
            }`,
            primaer: (
              <Button type="button" className="!px-5 !py-3 !text-[13.5px]" onClick={() => nav(abrechnungPfad(event.id))} data-testid="storno-hinweis-rueckerstattung-pruefen">
                <Icon name="undo" className="text-[18px]" /> Rückerstattung prüfen
              </Button>
            ),
            testId: "storno-hinweis-banner",
          };
        return { ton: "ruhig", icon: "event_busy", titel: "Abgesagt", zeile: "War das ein Irrtum? Über das Menü „⋯“ → Status ändern lässt sich die Absage zurücknehmen.", testId: "aktionskarte" };
      case "refunded":
        return { ton: "ruhig", icon: "currency_exchange", titel: "Vollständig erstattet", zeile: "Den Status ändern: Menü „⋯“ oben.", testId: "aktionskarte" };
      default:
        return { ton: "ruhig", icon: "info", titel: NEXT_STEP_COPY[event.status] ?? "Status prüfen", testId: "aktionskarte" };
    }
  })();

  /** Ein Eintrag aus „Status ändern“ (Menü „⋯“) — dieselben Wege wie das frühere Status-Menü. */
  function statusWaehlen(to: EventStatus) {
    // Antoine 26.09.2026: bei einer frühen Anfrage heißt dieser Eintrag „Verschieben …“ und
    // storniert NICHT (Terminvorschlag).
    if (to === "cancelled" && verschiebenMoeglich) setVerschiebenOffen(true);
    // H8: „Abgesagt“ läuft über den vollen Storno-Workflow (Payment-Void + Rollen-Gate).
    else if (to === "cancelled") void handleStorno();
    // Punkt 98: aus „Abgesagt“ heraus nie ohne Begründung.
    else if (event?.status === "cancelled") {
      setAbsageZurueckGrund("");
      setAbsageZurueckZiel(to);
    }
    // E0.8: Warum ein Angebot scheitert, weiß nur der Betreiber — darum hier fragen.
    else if (to === "offer_declined") {
      setDeclineWahl("");
      setDeclineFrei("");
      setDeclineOpen(true);
    }
    // „Zusagen mit neuer Fassung“ (Antoine, 04.11.2026): ist die versendete Fassung gesperrt (sql/770), sagt der Dialog klar,
    // was passiert; der Server hält die aktuelle Fassung fest (ohne Mail an den Gast) und sagt danach zu.
    else if ((to === "offer_chosen" || to === "paid") && event?.status === "offer_sent" && event.offerFassungVeraltetSeit) {
      void bestaetigen({ titel: ZUSAGE_NEUE_FASSUNG_TITEL, text: ZUSAGE_NEUE_FASSUNG_HINWEIS, bestaetigen: ZUSAGE_NEUE_FASSUNG_KNOPF }).then((ja) => {
        if (ja) void transition(to, undefined, true);
      });
    } else void transition(to);
  }

  return (
    <>
      {bestaetigungsDialog}
      {varianteDialog}
      {/* Kopf „Salon“ (Antoine, 26.09.2026): Kalenderblatt, Titel in Newsreader, Status, Kunde,
          rechts Sprach-Pille · Bearbeiten · Menü „⋯“, darunter die Faktenkärtchen. */}
      <VorgangKopf
        titel={anlassAnzeige(event.occasion) ?? "Vorgang"}
        status={kopfStatus}
        hinweis={terminHinweis}
        datum={event.eventDate}
        wunschMonat={wunschMonat}
        onDatumSpeichern={(wert) => void kopfDatumSpeichern(wert)}
        kunde={kundenBezeichnung}
        kundeZusatz={kundeZusatz}
        kontakt={{ telefon: customer?.phone ?? null, email: customer?.email ?? null }}
        nebenStatus={
          <>
            {zahlungsMeta && <StatusPill meta={zahlungsMeta} testId={zahlungsMeta.tone === "warn" ? "rest-offen" : "zahlungsstand-abzeichen"} />}
            {zahlwegHinweis && <StatusPill meta={{ label: zahlwegHinweis.label, tone: "neutral", icon: zahlwegHinweis.icon, title: zahlwegHinweis.title }} testId="zahlweg-hinweis" />}
            {angebotQuelle && istALaCarteOption(angebotQuelle) && (angebotQuelle.amountTotalCents ?? 0) <= 0 && (
              <StatusPill meta={{ label: A_LA_CARTE_VOR_ORT_KOPF, tone: "neutral", icon: "restaurant" }} testId="a-la-carte-abzeichen" />
            )}
            {opentableQ.data?.eingetragen && (
              <StatusPill meta={{ label: "OpenTable", tone: opentableQ.data.sichtbar && opentableQ.data.geaendertSeitEintrag ? "warn" : "neutral", icon: "table_restaurant" }} testId="opentable-abzeichen" />
            )}
            <GewonnenKonfetti justCelebrated={event.justCelebrated === true} />
          </>
        }
        rueckweg={
          // Punkt 115 (G2/G3): fester Breadcrumb zur Liste, in der der Fall je nach Phase steht,
          // plus „← Zurück zu <Herkunft>“ bei `?von=`. A1: Firma/Gastname statt Kurz-UUID.
          <Rueckweg className="min-w-0" eltern={vorgangsListe(event)} aktuell={<span data-testid="breadcrumb-vorgang">{kopfBezeichnung}</span>} />
        }
        fakten={fakten}
        aktionen={
          <>
            {/* Punkt 41 + E2: die Kommunikationssprache als Pille, bei Englisch blau. */}
            <SprachWahl
              darstellung="pille"
              eventId={event.id}
              eventNumber={event.number}
              sprache={event.offerLanguage}
              quelle={event.offerLanguageQuelle}
              kundeName={customer?.company || customer?.name}
              onGeaendert={() => {
                eventQ.reload();
                customerQ.reload();
                activityQ.reload();
              }}
            />
            <Button type="button" variant="secondary" className="!hidden !py-2 sm:!inline-flex" onClick={() => setEditOpen(true)}>
              <Icon name="edit" className="text-[18px]" /> Bearbeiten
            </Button>
            {/* E6: Küchenzettel, Status ändern und die seltenen Wege (Stornieren, Löschen) im Menü. */}
            <Aufklapper
              className="w-64"
              ausloeser={({ offen, umschalten }) => (
                <Button
                  type="button"
                  variant="secondary"
                  className="!h-9 !w-9 !rounded-full !p-0 sm:!h-auto sm:!w-auto sm:!rounded-lg sm:!px-2.5 sm:!py-2"
                  aria-label={offen ? "Weitere Aktionen schließen" : "Weitere Aktionen"}
                  aria-haspopup="menu"
                  aria-expanded={offen}
                  data-testid="vorgang-menue"
                  onClick={umschalten}
                >
                  <Icon name="more_horiz" className="text-[18px]" />
                </Button>
              )}
            >
              {(schliessen) => (
                <>
                  <MenueEintrag
                    icon="edit"
                    className="sm:hidden"
                    onClick={() => {
                      schliessen();
                      setEditOpen(true);
                    }}
                  >
                    Vorgang bearbeiten
                  </MenueEintrag>
                  <MenueEintrag
                    icon="request_quote"
                    onClick={() => {
                      schliessen();
                      nav(angebotPfad(event.id));
                    }}
                  >
                    Angebots-Builder öffnen
                  </MenueEintrag>
                  {/* A6/E7 + E6: der Küchenzettel ist ein Werkzeug der Durchführung. Am
                      Veranstaltungstag und bei „Bezahlt“ steht er in der Aktionskarte, sonst hier. */}
                  {!kuechenzettelInKarte && (
                    <MenueEintrag
                      icon="print"
                      disabled={printBusy}
                      data-testid="kuechenzettel-knopf"
                      title="Küchenzettel drucken (Positionen der gewählten Variante, ohne Preise)"
                      onClick={() => {
                        schliessen();
                        void printKitchenSheet();
                      }}
                    >
                      Küchenzettel drucken
                    </MenueEintrag>
                  )}
                  <div className="my-1.5 border-t border-outline-variant/30 dark:border-outline/20" />
                  <MenueTitel>Status ändern</MenueTitel>
                  {ALLOWED[event.status].length === 0 ? (
                    <p className="px-3.5 py-2 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">Endstatus – keine Übergänge mehr.</p>
                  ) : (
                    ALLOWED[event.status].map((to) => (
                      <MenueEintrag
                        key={to}
                        disabled={busy}
                        ton={to === "cancelled" && !verschiebenMoeglich ? "gefahr" : "normal"}
                        icon={to === "cancelled" && verschiebenMoeglich ? "event_repeat" : undefined}
                        data-testid={to === "cancelled" && verschiebenMoeglich ? "status-menue-verschieben" : `status-menue-${to}`}
                        onClick={() => {
                          schliessen();
                          statusWaehlen(to);
                        }}
                      >
                        {to === "cancelled" && verschiebenMoeglich ? "Verschieben …" : EVENT_STATUS[to].label}
                      </MenueEintrag>
                    ))
                  )}
                  {(stornoImMenue || isOwner) && <div className="my-1.5 border-t border-outline-variant/30 dark:border-outline/20" />}
                  {/* Storno bleibt erreichbar, auch wo „Abgesagt“ nicht in der Statusliste steht
                      (Durchgeführt, oder frühe Anfrage, wo der Eintrag „Verschieben …“ heißt). */}
                  {stornoImMenue && (
                    <MenueEintrag
                      icon="cancel"
                      ton="gefahr"
                      disabled={busy}
                      onClick={() => {
                        schliessen();
                        void handleStorno();
                      }}
                    >
                      Vorgang stornieren
                    </MenueEintrag>
                  )}
                  {/* Antoine 26.09.2026: „Zusätzlich braucht es noch den Punkt löschen (mit
                      Warnhinweis …)“ — nur für den Inhaber (der Server prüft Rolle UND Nummer). */}
                  {isOwner && (
                    <MenueEintrag
                      icon="delete_forever"
                      ton="gefahr"
                      data-testid="status-menue-loeschen"
                      disabled={hardDeleteBusy}
                      onClick={() => {
                        schliessen();
                        setHardDeleteInput("");
                        setHardDeleteFehler(null);
                        setHardDeleteOffen(true);
                      }}
                    >
                      Löschen …
                    </MenueEintrag>
                  )}
                </>
              )}
            </Aufklapper>
          </>
        }
      />

      {/* Mimmo 04.11.2026: „Geändert – bitte neu senden“ ist ein Hinweis, kein Fehler (ruhige Karte mit „Jetzt neu senden“).
          Steht die Aktionskarte (Status „gesendet“) schon mit demselben Hinweis da, doppelt er sich hier nicht. */}
      {actionError && !(istFassungNeuSendenMeldung(actionError) && event.status === "offer_sent" && event.offerFassungVeraltetSeit) && (
        <div className="mt-4">
          <FehlerOderHinweis message={actionError} zumSenden={angebotSendenPfad(event.id)} />
        </div>
      )}

      {/* S6: ohne Aktionskarte (Status „Durchgeführt“) steht die Sperre hier, direkt über dem Inhalt. */}
      {sperre && !aktion && (
        <div className="mt-4">
          <SperreVorOrt sperre={sperre} eventId={event.id} onNeuLaden={sperreNeuLaden} onTerminAendern={() => { setSperre(null); eckdatenOeffnen(false); }} onGueltigkeitVerlaengern={() => { setSperre(null); setGueltigkeitOffen(true); }} testId="vorgang-sperre" />
        </div>
      )}

      <div className="mt-6 flex flex-col gap-5">
        {/* Stepper + Aktionskarte. F5: für „Durchgeführt“ gibt es keinen nächsten Schritt mehr —
            dort steht eine ruhige, gestrichelt umrandete Abschlusszeile. */}
        {event.status === "completed" ? (
          <Card className="border-dashed p-5" data-testid="event-completed-header">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p
                  className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim"
                  data-testid="completed-header-vorgang"
                >
                  Vorgang {kundenBezeichnung ?? (event.number ?? `#${event.id.slice(0, 8)}`)} · abgeschlossen
                </p>
                <p className="mt-1 font-serif text-[20px] font-medium text-on-background dark:text-inverse-on-surface">
                  {titelAnzeige(event.occasion ?? "Vorgang")}
                </p>
                <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {formatWeekdayDate(event.eventDate)}
                  {event.guestCount != null ? ` · ${event.guestCount} Gäste` : ""}
                  {event.amountTotalCents != null ? ` · ${money(event.amountTotalCents)}` : ""}
                </p>
              </div>
              {/* Google-Bewertungsbitte (sql/542): nach der Veranstaltung rechts in dieser
                  Abschlusszeile, neben der Status-Pille — dort, wo vorher „Nächster Schritt“ stand. */}
              <div className="flex flex-wrap items-center gap-3">
                <BewertungsbitteSchalter eventId={event.id} />
                <StatusPill meta={EVENT_STATUS.completed} />
              </div>
            </div>
          </Card>
        ) : (
          aktion && (
            <Card className="flex flex-col gap-5 p-4 sm:p-6">
              <VorgangSchritte
                schritte={SCHRITTE}
                aktuell={stepIdx}
                ausserhalb={EVENT_STATUS[event.status].label}
                zahlung={schrittZahlungArt ? { key: "paid", art: schrittZahlungArt } : null}
              />
              <AktionsKarte
                {...aktion}
                zusatz={
                  <>
                  {sperre && <SperreVorOrt sperre={sperre} eventId={event.id} onNeuLaden={sperreNeuLaden} onTerminAendern={() => { setSperre(null); eckdatenOeffnen(false); }} onGueltigkeitVerlaengern={() => { setSperre(null); setGueltigkeitOffen(true); }} testId="aktionskarte-sperre" />}
                  <OpenTableAktion
                    sicht={opentableQ.data}
                    gast={{
                      name: customer?.name,
                      firma: customer?.company,
                      email: customer?.email,
                      telefon: customer?.phone,
                      anlass: anlassAnzeige(event.occasion),
                      anmerkung: event.customerNotes,
                    }}
                    busy={opentableBusy}
                    fehler={opentableFehler}
                    onMarkieren={(an) => void opentableMarkieren(an)}
                  />
                  </>
                }
              />
            </Card>
          )
        )}

        {/* E4: flache Zahlenleiste (Angebotswert · Zahlungen · Kostenübernahme) — ersetzt die fünf
            Kacheln UND die zweite Zahlenzeile der Abrechnungskarte. Die Hinweise der Abrechnung
            hängen darunter. id="abrechnung" trägt die Leiste selbst. */}
        <AuftragKennzahlen
          eventId={event.id}
          amountTotalCents={angebotswertCents}
          payments={payments}
          costAcceptance={caQ.data}
          abrechnungDeckung={event.abrechnungDeckung ?? null}
          angebotZeile={angebotZeile}
          aLaCarteOhneBetrag={alaCarteAngebot}
        >
          {/* P4 (21.10.2026): warum die Restzahlungsrechnung nicht entsteht, mit Weg — und wann der Zahlungslink an wen ging. */}
          {restzahlungHinweis && (
            <RestzahlungHinweis
              hinweis={restzahlungHinweis}
              onZurAbrechnung={() => nav(zurAbrechnungPfad(event))}
              onZumKunden={customer ? () => nav(mitHier(`/kunden/${customer.id}`, anlassAnzeige(event.occasion) ?? "Vorgang")) : undefined}
            />
          )}
          <ZahlungslinkVersand payments={payments} />
          {/* Punkt 29 P2 (freigegebenes Mockup 26.09.2026): der Hinweis nach geänderter Annahme. */}
          {planHinweis && (
            <div
              role="status"
              data-testid="zahlungsplan-hinweis"
              className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-lg bg-[#F0EAE0] px-3 py-2.5 text-[#6F675A] dark:bg-white/[0.08]"
            >
              <Icon name={planHinweis.art === "anpassen" ? "sync_problem" : "report"} className="mt-0.5 shrink-0 text-[18px]" />
              <div className="min-w-0 flex-1 basis-[calc(100%-2.5rem)] font-body-sm text-[12px] sm:basis-0">
                {planHinweis.art === "anpassen" ? (
                  <>
                    <p className="font-semibold">Annahme geändert – Zahlungsplan anpassen?</p>
                    <p className="mt-0.5 break-words">
                      Der Kunde hat die geänderte Fassung über {money(planHinweis.angebotCents)} angenommen, der Zahlungsplan steht noch auf{" "}
                      {money(planHinweis.planCents)}.{" "}
                      {planHinweis.bezahltVorhanden
                        ? "Bereits bezahlte Raten bleiben, nur die offenen Raten ändern sich."
                        : "Die Differenz wird anteilig auf die offenen Raten verteilt."}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">Annahme geändert: neuer Betrag liegt unter dem bereits Bezahlten</p>
                    <p className="mt-0.5 break-words">
                      Neu angenommen {money(planHinweis.angebotCents)}, bezahlt {money(planHinweis.bezahltCents)}. Überschuss{" "}
                      <b>{money(planHinweis.ueberschussCents)}</b>. Ein Umverteilen hilft hier nicht, der Überschuss muss erstattet werden. Die
                      bezahlte Anzahlungsrechnung bleibt bestehen, der Überschuss wird in der Abrechnung verrechnet oder erstattet.
                    </p>
                  </>
                )}
              </div>
              <div className="shrink-0 pl-[1.875rem] sm:pl-0">
                {planHinweis.art === "anpassen" ? (
                  <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" onClick={() => setPlanAnpassenOffen(true)}>
                    <Icon name="tune" className="text-[15px]" /> Plan anpassen …
                  </Button>
                ) : planHinweis.erstatten ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="!py-1.5 !text-[11.5px]"
                    onClick={() => nav(`${abrechnungPfad(event.id)}?erstatten=${planHinweis.erstatten!.paymentId}&betrag=${planHinweis.erstatten!.betragCents}`)}
                  >
                    <Icon name="undo" className="text-[15px]" /> Überschuss erstatten …
                  </Button>
                ) : (
                  <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" onClick={() => nav(zurAbrechnungPfad(event))}>
                    <Icon name="receipt_long" className="text-[15px]" /> Zur Abrechnung
                  </Button>
                )}
              </div>
            </div>
          )}
          {deckungshinweis && !planHinweis && (
            <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
              <Icon name="info" className="mt-px shrink-0 text-[14px]" />
              <span className="min-w-0 break-words">{deckungshinweis}</span>
            </p>
          )}
          {/* Punkt 29 P2, Entscheidung 3: Raten, deren Link die Anpassung entwertet hat. Der neue
              Link geht erst auf Klick hinaus — nie automatisch. */}
          {ohneLink.length > 0 && (
            <ul className="space-y-2" aria-label="Raten ohne gültigen Zahlungslink" data-testid="raten-ohne-link">
              {ohneLink.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-col gap-2 rounded-lg border border-outline-variant/30 p-2.5 sm:flex-row sm:items-center sm:justify-between dark:border-outline/30"
                >
                  <div className="min-w-0">
                    <p className="font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                      {PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType} · {money(p.amountCents)}
                    </p>
                    <p className="mt-0.5 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
                      {p.dueDate ? `Fällig ${formatDate(p.dueDate)} · ` : ""}alter Link ungültig
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    className="!py-1.5 !text-[11.5px]"
                    disabled={linkBusy !== null}
                    onClick={() => void neuenZahlungslinkSenden(p)}
                  >
                    <Icon name="send" className="text-[15px]" /> {linkBusy === p.id ? "Wird gesendet …" : "Neuen Zahlungslink senden"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {linkFehler && <ErrorNote message={linkFehler} />}
          {/* Einheit E (26.10.2026): bezahlte Zahlung ohne versendete Rechnung, mit Knopf „Rechnung senden“. */}
          <RechnungOffenHinweis payments={payments} verlauf={activityQ.data ?? []} probelauf={event.isTest === true} istInhaber={isOwner} onSenden={setRechnungZahlung} />
          <DankmailFehler
            payments={payments}
            onErneutSenden={async (p) => {
              try {
                await apiMutate(`/api/payments/${p.id}/dank-mail-erneut`, "POST", {});
              } finally {
                paymentsQ.reload();
                activityQ.reload();
              }
            }}
          />
          {rueckfallHinweis && (
            <div
              className="flex flex-wrap items-center gap-2 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]"
              data-testid="erstattung-rueckfall-hinweis"
            >
              <Icon name="undo" className="mt-px shrink-0 text-[14px]" />
              <span className="min-w-0 flex-1 break-words">{rueckfallHinweis}</span>
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void transition(rueckfallZiel)}>
                Status auf „{EVENT_STATUS[rueckfallZiel].label}“ setzen
              </Button>
            </div>
          )}
          {/* Antoine 14.10.2026: Restzahlungsrechnung sofort anlegen und senden, ohne Dialog. */}
          {zeigeRestzahlungKnopf(payments, event.status, isOwner) && <RestzahlungAnlegenKnopf eventId={event.id} onHinweis={setRestzahlungHinweis} onErstellt={() => paymentsQ.reload()} />}
        </AuftragKennzahlen>
        <RechnungSendenDialog
          open={rechnungZahlung != null}
          zahlung={rechnungZahlung}
          kundeName={customer?.name ?? null}
          grund={rechnungZahlung ? rechnungOffenGrund(rechnungZahlung, activityQ.data ?? []) : null}
          vorStichtag={rechnungZahlung ? rechnungZahlung.rechnung?.nachStichtag !== true : false}
          onClose={() => setRechnungZahlung(null)}
          onFertig={() => {
            setRechnungZahlung(null);
            paymentsQ.reload();
            activityQ.reload();
          }}
        />
        <ZahlungsplanAnpassenDrawer
          open={planAnpassenOffen}
          onClose={() => setPlanAnpassenOffen(false)}
          eventId={event.id}
          amountTotalCents={event.amountTotalCents}
          payments={payments}
          onAngepasst={() => {
            paymentsQ.reload();
            activityQ.reload();
          }}
        />

        {/* Hauptspalte (Anfrage als Brief, Angebot, Verlauf) und Seitenspalte (Kunde, Eckdaten,
            Notizen …). Die Seitenspalte bekommt 380 px erst ab xl — bei 1024 px stehen die Karten
            untereinander in voller Breite statt in einer 380 px schmalen Hauptspalte. */}
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px] 2xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="flex min-w-0 flex-col gap-5">
            {/* L2: Gespräch (28.09.2026) — ersetzt die bisherige "Anfrage"-Karte: das ganze
                Gespräch (alle Mails als Chat-Blasen) statt nur der ersten Nachricht. */}
            <AuftragGespraechKarte
              eventId={event.id}
              inquiryId={event.inquiryId ?? null}
              customerNotes={event.customerNotes ?? null}
              originChannel={event.originChannel ?? null}
              serviceKind={event.serviceKind ?? null}
              deliveryAddress={event.deliveryAddress ?? null}
              inquiryLanguage={event.inquiryLanguage ?? null}
              inquiryTranslation={event.inquiryTranslation ?? null}
              herkunft={event.herkunft ?? null}
              inquiryIsTest={event.inquiryIsTest ?? false}
              onChanged={() => eventQ.reload()}
              onTranslated={() => eventQ.reload()}
              emails={emailsQ.data ?? []}
              emailsLoading={emailsQ.loading}
              verlaufEintraege={verlaufEintraege}
              kundenName={customer?.name?.trim() || event.customerName?.trim() || null}
              kundenFirma={customer?.company?.trim() || null}
              kundenEmail={customer?.email ?? null}
              vorgangInfo={[anlassAnzeige(event.occasion), event.eventDate ? formatDate(event.eventDate) : null, anzeigeNummer].filter(Boolean).join(" · ") || null}
              onGesendet={() => {
                emailsQ.reload();
                activityQ.reload();
              }}
              onZuordnungGeaendert={() => emailsQ.reload()}
            />

            {/* L3: Angebot — die Varianten wie eine Speisekarte (Titel, Preislinie, Betrag). */}
            <Card className="p-5 sm:p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Icon name="restaurant_menu" className="text-[19px] text-primary" />
                  <h3 className="text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Angebot</h3>
                  {event.currentOfferVersion > 0 && (
                    <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                      v{event.currentOfferVersion}
                    </span>
                  )}
                </div>
                <Link to={angebotPfad(event.id)} className="inline-flex items-center gap-1 text-[12.5px] font-bold text-primary hover:underline">
                  Im Builder bearbeiten <Icon name="arrow_forward" className="text-[16px]" />
                </Link>
              </div>
              {offersQ.loading ? (
                <LoadingRows rows={2} />
              ) : offers.length === 0 ? (
                /* F1: Stammt dieser Vorgang aus einer Anfrage und ist noch im Entwurfs-Stand, stehen
                   hier die BEIDEN Erzeugen-Wege (+ der Dubletten-Hinweis, dessen Auswahl sonst
                   wirkungslos wäre). Der orange Knopf der Seite sitzt in der Aktionskarte — hier
                   deshalb beide Wege als Zweitknöpfe (Regel „ein oranger Knopf je Status“). */
                zeigtUebernahme ? (
                  <div className="space-y-4">
                    <EmptyState
                      icon="request_quote"
                      title="Noch kein Angebot"
                      hint="Aus dieser Anfrage einen Entwurf erzeugen lassen oder im Builder selbst zusammenstellen."
                    />
                    <DublettenHinweisKarte
                      inquiryId={event.inquiryId ?? null}
                      gewaehlteKundeId={uebernahmeKundeId}
                      onKundeGewaehlt={setUebernahmeKundeId}
                    />
                    {uebernahme.formError && <ErrorNote message={uebernahme.formError} />}
                    <div className="flex flex-wrap items-center justify-center gap-2.5">
                      {zeigtKiUebernahme && (
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={uebernahme.generating || uebernahme.creatingManually}
                          onClick={() => void uebernahme.generateOffer()}
                        >
                          <Icon name="auto_awesome" className="text-[18px]" /> KI-Angebot erzeugen
                        </Button>
                      )}
                      {/* Wie auf der gelöschten Anfrage-Detailseite (Antoine, 02.09.2026): der manuelle
                          Weg braucht weder Gästezahl noch einen bestimmten Stand und bleibt deshalb
                          immer erreichbar, solange kein Angebot existiert — auch bei „Abgesagt“. */}
                      <Button
                        type="button"
                        /* Ohne KI-Weg (z. B. „Abgesagt“) ist das die einzige Handlung der Seite — die
                           Aktionskarte hat dann keinen orangen Knopf, deshalb hier primary. */
                        variant={zeigtKiUebernahme ? "secondary" : "primary"}
                        disabled={uebernahme.generating || uebernahme.creatingManually}
                        onClick={() => void uebernahme.createManually()}
                      >
                        <Icon name="edit_note" className="text-[18px]" /> Manuell erstellen
                      </Button>
                    </div>
                    <p className="text-center font-body-sm text-[11px] leading-relaxed text-on-surface-variant/70">
                      Die KI stellt aus Anfrage, Katalog &amp; Paketen einen Entwurf samt Anschreiben zusammen — gesendet
                      wird erst nach deiner Freigabe.
                    </p>
                    <AngebotErzeugungModal
                      open={uebernahme.generating}
                      genStep={uebernahme.genStep}
                      guests={event.guestCount ?? null}
                      onAbbrechen={uebernahme.cancelGenerate}
                    />
                  </div>
                ) : (
                  <EmptyState icon="request_quote" title="Noch kein Angebot" hint="Im Angebots-Builder Optionen anlegen und an den Kunden senden." />
                )
              ) : (
                <div className="space-y-3">
                  {offers.map((o) => (
                    <div
                      key={o.id}
                      className={`rounded-xl border px-4 py-3.5 ${
                        o.isChosen
                          ? "border-primary-fixed-dim bg-primary-container/30 dark:border-primary/40 dark:bg-primary/10"
                          : "border-surface-variant bg-surface-container-low/60 dark:border-white/10 dark:bg-white/[0.03]"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-secondary-fixed-dim">
                          {o.guestCount} Gäste · v{o.version}
                          {o.guestCount > 0 ? ` · ${money(Math.round(o.amountTotalCents / o.guestCount))} pro Gast` : ""}
                        </span>
                        {o.isChosen ? (
                          <span className="flex items-center gap-1 font-label-md text-label-md font-bold text-primary">
                            <Icon name="check_circle" className="text-[18px]" /> Gewählt
                          </span>
                        ) : event.status === "offer_sent" ? (
                          <StatusPill meta={{ label: "Wartet auf Auswahl", tone: "warn" }} />
                        ) : null}
                      </div>
                      {/* Speisekarten-Zeile: Titel links, gepunktete Linie, Betrag rechts. */}
                      <div className="mt-1 flex items-end gap-2">
                        <span className="min-w-0 font-serif text-[18px] leading-snug text-on-background dark:text-inverse-on-surface">
                          {o.title || money(o.amountTotalCents)}
                          {o.menuSelection?.title ? ` · ${o.menuSelection.title}` : ""}
                        </span>
                        <span aria-hidden className="mb-[7px] min-w-[24px] flex-1 border-b-[1.5px] border-dotted border-outline dark:border-white/25" />
                        <span className="shrink-0 text-[17px] font-extrabold tabular-nums text-on-background dark:text-inverse-on-surface">
                          {money(o.amountTotalCents)}
                        </span>
                      </div>
                      {/* WARTESCHLANGE Punkt 50: wer hat zugesagt — der Kunde online oder der Betrieb
                          telefonisch? Die Angabe wird nicht neu erhoben, nur gezeigt. */}
                      {o.isChosen && o.agbAccepted && (() => {
                        const vomBetrieb = betriebsseitigeZusage(o.chosenUserAgent);
                        return (
                          <p className="mt-2 flex items-start gap-1.5 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
                            <Icon name={vomBetrieb === null ? "verified" : "support_agent"} className="mt-0.5 shrink-0 text-[16px]" />
                            <span>
                              Verbindlich angenommen {formatDateTime(o.agbAcceptedAt)}
                              {vomBetrieb === null
                                ? o.chosenIp
                                  ? ` · IP ${o.chosenIp}`
                                  : ""
                                : vomBetrieb
                                  ? ` · telefonisch, eingetragen von ${vomBetrieb}`
                                  : " · telefonisch, vom Betrieb eingetragen"}
                            </span>
                          </p>
                        );
                      })()}
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline disabled:opacity-50"
                        disabled={printBusy}
                        onClick={() => void printOfferDocument(o.id)}
                      >
                        <Icon name="picture_as_pdf" className="text-[16px]" /> Dokument (PDF)
                      </button>
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    {event.publicToken && event.offerSentAt ? (
                      <a
                        /* Betreiber-Ansicht statt Gast-URL — hier sitzt der PDF-Knopf und der Probelauf
                           (Antoine, 21.09.2026); der Aufruf zählt nicht als Gast-Aufruf. */
                        href={angebotsseitePfad(event.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 font-label-md text-label-md text-primary hover:underline"
                      >
                        <Icon name="open_in_new" className="text-[18px]" /> Angebot ansehen · als PDF sichern
                      </a>
                    ) : (
                      <span />
                    )}
                    <Link
                      to={angebotPfad(event.id)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-outline-variant px-3 py-1.5 font-label-md text-label-md text-secondary hover:bg-surface-container-high dark:border-outline dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/20"
                    >
                      <Icon name="add" className="text-[16px]" /> Weitere Option anbieten
                    </Link>
                  </div>
                </div>
              )}
            </Card>

            {/* Punkt 76 (Antoine, 20.09.2026): die Karte „Rückfragen“ ist entfallen. */}

            {/* L5: Verlauf (WP1) — Aktivität + Versionsarchiv + Vorgangsansicht in einer Karte. */}
            {id && (
              <AuftragVerlaufKarte
                eventId={id}
                loading={emailsQ.loading || activityQ.loading || historyQ.loading}
                eintraege={verlaufEintraege}
                emails={emailsQ.data ?? []}
                onVersionKlick={setVorschauVersion}
                onEckdatenKlick={() => setFeldVersionenDrawerOffen(true)}
              />
            )}
          </div>

          {/* Seitenspalte — unter xl zweispaltig, damit Kunde und Eckdaten nebeneinander stehen. */}
          <div className="grid min-w-0 grid-cols-1 items-start gap-5 md:grid-cols-2 xl:flex xl:flex-col xl:items-stretch">
            {/* R2: Kunde — PR4: VOR den Eckdaten. DIN-5008-artige Adresse (Firma → Name → Straße →
                PLZ Ort), reine Anzeige; gepflegt wird im Kundenprofil. */}
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Kunde</h3>
                {customer && (
                  <Link
                    to={mitHier(`/kunden/${customer.id}`, anlassAnzeige(event.occasion) ?? "Vorgang")}
                    className="inline-flex items-center gap-1 text-[12.5px] font-bold text-primary hover:underline"
                  >
                    Kundenprofil öffnen
                    <Icon name="arrow_forward" className="text-[16px]" />
                  </Link>
                )}
              </div>
              {customer ? (
                <div>
                  {customer.company && <p className="text-[15px] font-bold text-on-background dark:text-inverse-on-surface">{customer.company}</p>}
                  <p
                    className={
                      customer.company
                        ? "font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim"
                        : "text-[15px] font-bold text-on-background dark:text-inverse-on-surface"
                    }
                  >
                    {customer.name}
                  </p>
                  {/* Punkt 42: Kundentyp (am Kunden gespeichert; ändern auf der Kundenseite). */}
                  <p data-testid="vorgang-kundentyp" className="mt-2 flex flex-wrap items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name={KUNDENTYP_ICON[kundentypVon(customer)]} className="text-[18px]" /> {KUNDENTYP_TEXT[kundentypVon(customer)]}
                    {customer.kundentypAutomatisch && <AutomatischErkanntChip text="automatisch erkannt" />}
                    <Link to={mitHier(`/kunden/${customer.id}`, anlassAnzeige(event.occasion) ?? "Vorgang")} className="text-[11.5px] text-primary hover:underline">
                      Typ ändern
                    </Link>
                  </p>
                  <ul className="mt-2">
                    {(
                      <KundenZeile
                        icon="location_on"
                        ton="neutral"
                        label="Adresse"
                        bearbeiten={(schliessen) => (
                          <AdresseFenster strasse={customer.addressStreet ?? null} plz={customer.addressZip ?? null} stadt={customer.addressCity ?? null} speichern={kundePatch} schliessen={schliessen} />
                        )}
                      >
                        {!(customer.addressStreet || customer.addressZip || customer.addressCity) && <span className="font-medium text-secondary-fixed-dim">Adresse ergänzen</span>}
                        {customer.addressStreet && <span className="block">{customer.addressStreet}</span>}
                        {(customer.addressZip || customer.addressCity) && (
                          <span className="block">{[customer.addressZip, customer.addressCity].filter(Boolean).join(" ")}</span>
                        )}
                      </KundenZeile>
                    )}
                    <KundenZeile
                      icon="mail"
                      ton="info"
                      label="E-Mail"
                      bearbeiten={(schliessen) => <TextFenster label="E-Mail" name="email" type="email" pflicht maxLength={320} wert={customer.email} speichern={kundePatch} schliessen={schliessen} />}
                    >
                      <span className="block break-all">{customer.email}</span>
                    </KundenZeile>
                    <KundenZeile
                      icon="call"
                      ton="success"
                      label="Telefon"
                      bearbeiten={(schliessen) => <TextFenster label="Telefon" name="phone" type="tel" maxLength={64} wert={customer.phone ?? null} speichern={kundePatch} schliessen={schliessen} />}
                    >
                      {customer.phone ? <span className="tabular-nums">{customer.phone}</span> : <span className="font-medium text-secondary-fixed-dim">Telefon ergänzen</span>}
                    </KundenZeile>
                  </ul>
                  <ButtonLink href={`mailto:${customer.email}`} variant="secondary" className="mt-3 !py-1.5 !text-[11.5px]">
                    <Icon name="mail" className="text-[15px]" /> E-Mail schreiben
                  </ButtonLink>
                </div>
              ) : (
                <LoadingRows rows={1} />
              )}
            </Card>

            {/* R1: Eckdaten — E3: Anzeige hier, Felder vollbreit im Drawer „Eckdaten“. */}
            <EckdatenUebersicht
              eventId={event.id}
              status={event.status}
              aktivitaeten={activityQ.data ?? []}
              eventDate={event.eventDate}
              tageBis={terminHinweis}
              timeFrom={event.timeFrom}
              timeTo={event.timeTo}
              guestCount={event.guestCount}
              angebotGaeste={zielAngebot?.guestCount ?? null}
              ort={ortText}
              ortFehlt={ortFehlt}
              setupLocation={event.setupLocation ?? null}
              onBearbeiten={() => eckdatenOeffnen(false)}
              onGeoeffnet={() => eventQ.reload()}
              bearbeiter={{
                datum: (schliessen) => <DatumFenster datum={event.eventDate} speichern={vorgangPatch} schliessen={schliessen} />,
                uhrzeit: uhrzeitBearbeiten,
                gaeste: gaesteBearbeiten,
                ort: ortBearbeiten,
              }}
            />

            {/* R3: Notizen (Mimmo via Antoine 09.09.2026) — Termin-/Erinnerungs-Chips + Anlage-Drawer. */}
            {id && <AuftragNotizenKarte eventId={id} autoOpen={notizAutoOpen} />}

            {/* Google-Bewertungsbitte vor dem Abschluss: die Bitte geht auch bei „angenommen“/„bezahlt“
                raus (sql/542), bis zum Versand umschaltbar — nach dem Abschluss steht sie in der
                Abschlusszeile oben. */}
            {event.status !== "completed" && <BewertungsbitteSchalter eventId={event.id} />}

            {/* sql/772: Menüwahl nach der Buchung - Stand und „Menüauswahl an Gast senden“ (nur mit Menüwahl in der gebuchten Fassung). */}
            {["offer_chosen", "paid", "balance_due", "payment_failed"].includes(event.status) && <MenuewahlNachBuchungKarte eventId={event.id} />}

            {/* R1.2 — „Kennen wir schon?“ (F1): derselbe Block wie auf der Anfrage-Detailseite. */}
            <KundenhistorieBlock
              pfad={event.inquiryId ? `/api/inquiries/${event.inquiryId}/historie` : `/api/customers/${event.customerId}/historie`}
              ort="anfragen_detail"
            />

            {/* Reisegruppen-Widget T5: Monat, Gruppengröße und Nachgereichtes der Widget-Anfrage. */}
            {event.inquiryId && <AnfrageAngabenKarte inquiryId={event.inquiryId} angaben={event.anfrageAngaben} />}

            {/* R0.9 — Karte „Wer fragt hier an?“ (F2): nur mit `inquiryId` sinnvoll. */}
            {event.inquiryId && event.sourceNote && (
              <FirmenprofilKarte inquiryId={event.inquiryId} firma={customer?.company?.trim() || null} anfrageEingang={event.sourceNote.receivedAt} />
            )}

            {/* R2a: KI-Kunden-Briefing (E2), seit 28.09.2026 automatisch befuellt (kein Knopf mehr
                fuer den Normalfall — nur noch die Nebenaktion "Neu erzeugen"/"Erneut versuchen").
                Vier ehrliche Zustände: laedt, Kaltstart, unbelegte Zahl (unterdrückt), Text. */}
            {customer && (
              <Card className="p-5" data-testid="kunden-briefing">
                <div className="mb-3 flex items-center gap-2">
                  <Icon name="auto_awesome" className="text-[18px] text-primary" />
                  <h3 className="text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Kunden-Briefing</h3>
                </div>

                {briefingQ.loading && (
                  <p className="flex items-center gap-2 font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">
                    <Icon name="progress_activity" className="animate-spin text-[16px]" /> Kunden-Briefing wird geladen …
                  </p>
                )}

                {briefingQ.error && !briefingQ.loading && (
                  <ErrorNote message={briefingQ.error} title="Briefing konnte nicht geladen werden" onRetry={briefingQ.reload} />
                )}

                {briefing && !briefingQ.loading && !briefing.vorhanden && (
                  <p className="font-body-sm text-[13px] leading-relaxed text-secondary dark:text-secondary-fixed-dim" data-testid="kunden-briefing-neu">
                    Neukundschaft — mit dieser Kundin gab es bisher kein Angebot, keine Buchung und keine Rechnung. Deshalb
                    gibt es hier noch nichts zusammenzufassen.
                  </p>
                )}

                {briefing && !briefingQ.loading && briefing.vorhanden && briefing.ok && (
                  <div data-testid="kunden-briefing-text">
                    <p className="font-body-md text-[14px] leading-relaxed text-on-background dark:text-inverse-on-surface">{briefing.text}</p>
                    {briefingNeuFehler && <ErrorNote message={briefingNeuFehler} title="Neu erzeugen fehlgeschlagen" onRetry={() => void briefingNeuErzeugen()} />}
                    <Button
                      type="button"
                      variant="ghost"
                      className="mt-3 !px-0 !text-[11.5px]"
                      disabled={briefingNeuBusy}
                      onClick={() => void briefingNeuErzeugen()}
                    >
                      <Icon name={briefingNeuBusy ? "progress_activity" : "refresh"} className={briefingNeuBusy ? "animate-spin text-[15px]" : "text-[15px]"} />{" "}
                      {briefingNeuBusy ? "Wird neu erzeugt …" : "Neu erzeugen"}
                    </Button>
                  </div>
                )}

                {briefing && !briefingQ.loading && briefing.vorhanden && !briefing.ok && (
                  <div data-testid="kunden-briefing-unavailable">
                    <p className="font-body-sm text-[13px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
                      Briefing momentan nicht verfügbar — die KI-Antwort enthielt eine Zahl, die sich nicht in euren Daten
                      belegen ließ. Wir zeigen sie deshalb nicht als sichere Aussage an.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      className="mt-3 !py-1.5 !text-[11.5px]"
                      disabled={briefingNeuBusy}
                      onClick={() => void briefingNeuErzeugen()}
                    >
                      <Icon name={briefingNeuBusy ? "progress_activity" : "refresh"} className={briefingNeuBusy ? "animate-spin text-[15px]" : "text-[15px]"} />{" "}
                      {briefingNeuBusy ? "Wird erneut versucht …" : "Erneut versuchen"}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* R2b: Wetter (Datenanreicherung Welle A) — nur wenn tenant-weit aktiviert */}
            {wetterEnabled && (
              <Card className="p-5">
                <h3 className="mb-3 text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Wetter</h3>
                {wetter.zustand === "laedt" ? (
                  <LoadingRows rows={1} />
                ) : wetter.zustand !== "prognose" || !weatherQ.data ? (
                  <WetterMeldung
                    anzeige={wetter}
                    // V-1164: zeigte bis dahin auf `setEditOpen(true)` ("Vorgang bearbeiten") — der
                    // reduzierte Drawer hat seit WP3a weder Ort-Typ noch Ort mehr (s. Kommentar
                    // oben bei `eckdatenOffen`), jeder Klick lief also ins Leere. Ziel ist jetzt
                    // derselbe Eckdaten-Drawer wie beim „Ort ergänzen"-Chip im Kopf (Zeile ~1265) —
                    // mit Ort-Fokus ausser bei „Kein Termin" (da geht es um das Datum, nicht den Ort).
                    onBearbeiten={() => eckdatenOeffnen(wetter.zustand !== "kein_termin")}
                    onErneutVersuchen={() => weatherQ.reload()}
                  />
                ) : (
                  <div>
                    <p className="font-body-md text-body-md font-semibold capitalize text-on-background dark:text-inverse-on-surface">
                      {weatherQ.data.description ?? "—"}
                    </p>
                    <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      {weatherQ.data.tempMinC != null && weatherQ.data.tempMaxC != null
                        ? `${Math.round(weatherQ.data.tempMinC)}° – ${Math.round(weatherQ.data.tempMaxC)}°`
                        : "–"}
                      {weatherQ.data.precipitationMm != null && weatherQ.data.precipitationMm > 0
                        ? ` · ${weatherQ.data.precipitationMm} mm Niederschlag`
                        : ""}
                    </p>
                    {/* R4.3 + S2.1: die Wetter-Folgerung, fertig vom Server (deterministische Regel in
                        @maestro/wetter-anzeige, nicht vom Modell). Beschreibend, keine Sperre. */}
                    {weatherQ.zusatz?.folgerung?.saetze.map((satz) => (
                      <p
                        key={satz}
                        className="mt-2 rounded-lg bg-secondary-container/40 px-3 py-2 font-body-sm text-body-sm text-on-background dark:bg-surface-container-high dark:text-inverse-on-surface"
                      >
                        {satz}
                      </p>
                    ))}
                    {/* R1.6: Stand des Abrufs, minutengenau (Prognosen werden nicht gespeichert).
                        Migration 350/385: welche Adressstufe gegriffen hat — dieselbe
                        Vollständigkeits-Regel wie serverseitig, mit `.trim()` (Sicherheitsreview Fix C). */}
                    <p className="mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                      {event.locationType === "in_house"
                        ? "Betriebsadresse"
                        : event.deliveryAddressStreet?.trim() && event.deliveryAddressZip?.trim() && event.deliveryAddressCity?.trim()
                          ? "Lieferadresse"
                          : event.eventAddressStreet?.trim() && event.eventAddressZip?.trim() && event.eventAddressCity?.trim()
                            ? "Auftragsadresse"
                            : "Kundenadresse"}{" "}
                      · {standZeile("Bright Sky (DWD)", weatherQ.geladenAm, undefined, "minute")}
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* Antoine 12.10.2026: Tagesbelegung („ist der Tag schon voll?") auch auf der Vorgangsseite, nicht nur im Builder. */}
            <AngefragteTageKarte eventId={event.id} onDatumFestlegen={() => eckdatenOeffnen(false)} />

            {/* Backlog #41: Anhänge (Chat-Referenzfotos) — nur sichtbar, wenn welche vorliegen. */}
            {Boolean((event.details as { attachments?: EventAttachment[] } | null)?.attachments?.length) && (
              <Card className="p-5">
                <h3 className="mb-3 text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Anhänge</h3>
                <div className="space-y-2">
                  {((event.details as { attachments?: EventAttachment[] } | null)?.attachments ?? []).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg bg-surface-container-high px-3 py-2 text-left font-body-sm text-body-sm text-primary hover:underline dark:bg-white/[0.08]"
                      onClick={() => void openAuthenticatedFile(`/api/events/${id}/attachments/${a.id}`)}
                    >
                      <Icon name="image" className="text-[18px]" /> Anhang ansehen
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {cancellationTodos && (
              <div className="rounded-lg border border-outline-variant/40 bg-surface-container-low p-3 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30">
                <p>Storniert – {cancellationTodos.canceledPayments ?? 0} offene Zahlung(en) entwertet.</p>
                {(cancellationTodos.openInvoices?.length ?? 0) > 0 && (
                  <p className="mt-1">
                    Noch zu tun: {cancellationTodos.openInvoices!.length} offene Rechnung(en) stornieren (in der{" "}
                    <Link to={abrechnungPfad(event.id)} className="text-primary hover:underline">
                      Abrechnung
                    </Link>{" "}
                    „Stornieren").
                  </p>
                )}
                {(cancellationTodos.paidPayments?.length ?? 0) > 0 && (
                  <p className="mt-1">{cancellationTodos.paidPayments!.length} bezahlte Zahlung(en) ggf. erstatten.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Handy: Platz für den fest angehefteten Hauptknopf über der unteren Navigation. */}
      {aktion?.primaer && event.status !== "completed" && <div aria-hidden className="h-16 lg:hidden" />}

      {/* E3: Eckdaten im Drawer — dieselbe Karte, vollbreit, Speichern beim Verlassen jedes Feldes. */}
      <Drawer
        open={eckdatenOffen}
        onClose={() => setEckdatenOffen(false)}
        title="Eckdaten"
        subtitle={[titelAnzeige(event.occasion ?? "Vorgang"), kundenBezeichnung].filter(Boolean).join(" · ")}
        autosave
        footer={
          <Button type="button" variant="secondary" onClick={() => setEckdatenOffen(false)}>
            Schließen
          </Button>
        }
      >
        <AuftragEckdatenKarte
          imDrawer
          eventId={event.id}
          status={event.status}
          /* Punkt 29 P1: der Verlauf liegt hier ohnehin geladen. */
          aktivitaeten={activityQ.data ?? []}
          eventDate={event.eventDate}
          isPast={isPast}
          timeFrom={event.timeFrom}
          timeTo={event.timeTo}
          guestCount={event.guestCount}
          location={event.location}
          locationType={event.locationType}
          offSiteMode={event.offSiteMode}
          setupLocation={event.setupLocation}
          deliveryAddressStreet={event.deliveryAddressStreet}
          deliveryAddressZip={event.deliveryAddressZip}
          deliveryAddressCity={event.deliveryAddressCity}
          deliveryAddressCompany={event.deliveryAddressCompany}
          deliveryAddressName={event.deliveryAddressName}
          deliveryAddressAddition={event.deliveryAddressAddition}
          deliveryAddressCountryCode={event.deliveryAddressCountryCode}
          customerAddressStreet={customer?.addressStreet ?? null}
          customerAddressZip={customer?.addressZip ?? null}
          customerAddressCity={customer?.addressCity ?? null}
          customerCompany={customer?.company ?? null}
          customerName={customer?.name ?? null}
          customerAddressAddition={customer?.addressAddition ?? null}
          customerAddressCountryCode={customer?.addressCountryCode ?? null}
          inquiryDeliveryAddress={event.deliveryAddress ?? null}
          ortFokusToken={ortFokusToken}
          currentOfferVersion={event.currentOfferVersion}
          zielAngebot={zielAngebot}
          wonAt={event.wonAt ?? null}
          onEventChanged={() => {
            eventQ.reload();
            // Migration 350/361-Erbe: ein geändertes Datum bei unveränderter Wetter-URL löst sonst keinen Refetch aus.
            weatherQ.reload();
            // Gästezahl: der Server zieht die Varianten mit (lib/event-patch.ts).
            offersQ.reload();
          }}
          onOfferChanged={() => {
            offersQ.reload();
            // syncEventAmountTotal (offer-items.ts) kann events.amount_total_cents mitschreiben.
            eventQ.reload();
          }}
          onOpenBearbeiten={() => {
            setEckdatenOffen(false);
            setEditOpen(true);
          }}
          onOpenVerlauf={() => setFeldVersionenDrawerOffen(true)}
        />
      </Drawer>

      {/* B2 (UX-Polish 25.08.2026), extrahiert als eigene Komponente in WP0 (08.09.2026,
          Auftrags-Dashboard-Fundament) — Doku dazu in `ui/auftrag/VersionVorschauDrawer.tsx`. */}
      <VersionVorschauDrawer
        eintrag={vorschauVersion == null ? null : (historyQ.data ?? []).find((h) => h.version === vorschauVersion) ?? null}
        eventStatus={event.status}
        historyBusy={historyBusy}
        onClose={() => setVorschauVersion(null)}
        onRestore={restoreVersion}
      />

      {/* PR4 (docs/PLAN-EVENT-FELD-VERSIONIERUNG.md Abschnitt 4.1 Punkt 2): EINE Instanz fuer die
          gesamte Seite — geoeffnet sowohl vom Banner in `AuftragEckdatenKarte` als auch von der
          Sammelzeile in `AuftragVerlaufKarte`. */}
      {id && (
        <FeldVersionenDrawer
          eventId={id}
          status={event.status}
          open={feldVersionenDrawerOffen}
          onClose={() => setFeldVersionenDrawerOffen(false)}
          ersteAngebotsversionGesendetAm={ersteAngebotsversionGesendetAm}
          onRestored={() => {
            eventQ.reload();
            feldVersionenQ.reload();
            weatherQ.reload();
          }}
        />
      )}

      {/* Bearbeiten: Kernfelder des Vorgangs */}
      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title="Vorgang bearbeiten">
        <form className="space-y-4" onSubmit={(e) => void saveEdit(e)}>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Anlass</span>
            <Input name="occasion" defaultValue={event.occasion ?? ""} />
          </label>
          {/* Punkt 97 (Antoine, 23.09.2026): Datum/Uhrzeit auch hier — gesammelt per „Speichern“.
              `key` am Wert: der Drawer unmountet nicht, ein anderswo (Kopf, Eckdaten-Karte)
              geänderter Wert soll hier beim nächsten Öffnen stehen, nicht der alte. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" key={`dr-${event.eventDate ?? ""}-${event.timeFrom ?? ""}-${event.timeTo ?? ""}`}>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Datum</span>
              <DateInput name="eventDate" data-testid="drawer-datum" defaultValue={event.eventDate?.slice(0, 10) ?? ""} />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Uhrzeit</span>
              <span className="flex items-center gap-1.5">
                <span className="w-[90px]"><ZeitEingabe name="timeFrom" defaultValue={event.timeFrom?.slice(0, 5) ?? ""} /></span>
                <span aria-hidden>–</span>
                <span className="w-[90px]"><ZeitEingabe name="timeTo" defaultValue={event.timeTo?.slice(0, 5) ?? ""} /></span>
              </span>
            </label>
          </div>
          {/* A5/E2 (Antoine, 13.09.2026): das Feld „Art" (`events.service_type`) ist hier ERSATZLOS
              entfallen. Prod-Auswertung 13.09.2026: 263x leer, 45x `restaurant`, 12x `event`, 1x
              `catering` — durchweg maschinelle Steuerwerte — und nur 2x echter Freitext, bei denen
              `occasion` ohnehin gefuellt war. Fuer einen Menschen war das Feld ohne Aussage und
              doppelte sich mit „Anlass". **Die Spalte, die API und `resolvePillar`
              (`apps/api/src/lib/offer-briefing.ts`) bleiben unangetastet** — dort steuert der Wert
              weiterhin das KI-Briefing; entfallen ist nur die Pflege von Hand (auch aus dem
              PATCH-Payload in `saveEdit`, sonst haette ein Anlass-Save den Wert genullt).
              WP3a (Auftrags-Dashboard, 08.09.2026): Ort-Typ/OffSiteMode-Select, Datum/Uhrzeit,
              Gäste und der Ort-Freitext sind in die `AuftragEckdatenKarte` gewandert
              (eigenes Autosave dort) — dieser Drawer bleibt für Anlass/Stammdaten-Ort/
              Adresse dieses Auftrags/Angebotssprache. */}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Stammdaten-Ort</span>
            {/* Design-System-Migration P2: `editLocationId` haelt den Wert unabhaengig davon, ob
                `locationsQ.data` schon geladen ist (s. Reset-Effekt oben) — der fruehere
                `key`-Remount-Trick war ein Workaround genau fuer das native, `defaultValue`-basierte
                Auswahlfeld und entfaellt bei einem kontrollierten Radix-`Select`. */}
            <Select
              ariaLabel="Stammdaten-Ort"
              value={editLocationId}
              onValueChange={setEditLocationId}
              options={[
                { value: KEIN_WERT, label: "Kein Ort ausgewählt" },
                ...(locationsQ.data ?? []).map((loc) => ({
                  value: loc.id,
                  label: `${loc.name}${loc.isActive ? "" : " (inaktiv)"}`,
                })),
              ]}
            />
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Räume/Kapazitäten verwalten unter Einstellungen → Orte.
            </span>
          </label>

          {/* Migration 350 (Auftragsadresse, docs/KONZEPT-AUFTRAGSADRESSE.md § 5): eigene,
              vom Kunden abweichende Liefer-/Veranstaltungsadresse. Gesetzt (alle drei Felder),
              hat sie Vorrang vor der Kundenadresse fuer Wetter/Ort UND Rechnung - leer, gilt
              weiterhin die Kundenadresse fuer beides. Kein stillschweigendes Ersetzen: die
              Kundenadresse bleibt daneben lesbar sichtbar, klar beschriftet. */}
          <div className="space-y-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <div>
              <span className="block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Adresse dieses Auftrags
              </span>
              <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                Nur ausfüllen, wenn dieser Auftrag an einer anderen Adresse als der Kunde
                stattfindet/beliefert wird. Leer = es gilt weiterhin die Kundenadresse (für Wetter,
                Ort und Rechnung).
              </span>
            </div>
            {/* data-testid: "Ort" (city) kollidiert im selben Drawer mit dem bestehenden
                location-Feld weiter oben (ebenfalls beschriftet "Ort") - getByLabel waere dort
                mehrdeutig, siehe apps/e2e/README.md "Selektoren" - deshalb weiterhin explizite
                Test-IDs statt Label-Text (testIdPrefix="event-adresse" haelt die bereits
                bestehenden `event-adresse-strasse`/`-plz`/`-ort` unveraendert, s. `ui/AdresseFelder.tsx`
                Kopfkommentar zum Testid-Schema). Kontrolliert statt Autosave-onBlur: dieser
                Drawer speichert weiterhin gesammelt per "Speichern"-Knopf (`saveEdit`/`editAdresse`
                oben), nicht feldweise. */}
            <AdresseFelder
              werte={editAdresse}
              onFeldChange={onAdresseFeldChange}
              firmaName="betreiber"
              commitOn="change"
              laender={LAENDER}
              testIdPrefix="event-adresse"
            />
            {customer && (
              <p className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-container-high px-3 py-2 font-body-sm text-[11.5px] text-on-surface-variant dark:bg-white/[0.08]">
                <span>
                  <span className="font-semibold">Kundenadresse (Standard):</span>{" "}
                  {adresseEinzeilig({
                    name: customer.name,
                    company: customer.company,
                    street: customer.addressStreet,
                    addition: customer.addressAddition,
                    zip: customer.addressZip,
                    city: customer.addressCity,
                    countryCode: customer.addressCountryCode,
                  }) ?? "keine Adresse beim Kunden hinterlegt"}
                </span>
                <StatusPill
                  meta={landChip({ street: customer.addressStreet, zip: customer.addressZip, city: customer.addressCity, countryCode: customer.addressCountryCode, abgeleitetAm: customer.addressCountryAbgeleitetAm }) ?? undefined}
                />
              </p>
            )}
          </div>

          {/* Punkt 41: die Kommunikationssprache steht jetzt im Vorgangskopf (SprachWahl), nicht mehr hier. */}
          <div className="flex justify-end gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={editBusy}>
              {editBusy ? "Speichert …" : "Speichern"}
            </Button>
          </div>
        </form>
      </Drawer>

      {/**
        * E0.8 — Ablehnungsgrund erfassen.
        *
        * Die Liste ist eine Starthilfe, kein Zwang: „Anderer Grund" mit Freitext ist
        * gleichwertig, und „Ohne Grund ablehnen" bleibt möglich. Ein erzwungenes Pflichtfeld
        * würde nur Pseudogründe erzeugen — die wären schlimmer als gar keine.
        */}
      {/* Punkt 47: eine Rückfrage, die sagt was passiert — nicht „Sind Sie sicher?". */}
      <Modal
        open={annahmeZurueckOffen}
        onClose={() => {
          setAnnahmeZurueckOffen(false);
          setAnnahmeBelege([]);
        }}
        width="520px"
        title="Annahme zurücknehmen"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setAnnahmeZurueckOffen(false)} disabled={busy}>
              Abbrechen
            </Button>
            {annahmeBelege.length > 0 && (
              <span className="mr-auto self-center font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim" data-testid="annahme-erst-belege-erledigen">
                Erst die Belege oben erledigen
              </span>
            )}
            <Button type="button" onClick={() => void annahmeZuruecknehmen()} disabled={busy || annahmeBelege.length > 0}>
              Annahme zurücknehmen
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          Der Vorgang geht zurück auf <strong>„Angebot verschickt“</strong>. Die Zusage der Kundin gilt danach nicht mehr,
          die Auswahl der Variante und die Zustimmung zu den Bedingungen werden gelöscht — die Kundin kann über denselben
          Link erneut annehmen.
        </p>
        <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Hängen noch offene oder bezahlte Zahlungen, Rechnungen oder eine Kostenübernahme am Vorgang, zeigt MAESTRO sie
          einzeln und sagt, was jeweils zu tun ist. Stornierte und zurückgezogene Belege stören nicht.
        </p>
        <AnnahmeBelegeListe belege={annahmeBelege} />
      </Modal>
      {/* Punkt 98 (Antoine, 23.09.2026: „abgesagt ist ein fehler“): Absage zurücknehmen — Pflichtbegründung,
          landet im Verlauf; der Gast bekommt keine Mail. */}
      <Drawer open={absageZurueckZiel != null} onClose={() => setAbsageZurueckZiel(null)} title="Absage zurücknehmen">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!absageZurueckZiel) return;
            const ziel = absageZurueckZiel;
            setAbsageZurueckZiel(null);
            void transition(ziel, absageZurueckGrund);
          }}
        >
          <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
            Der Vorgang geht von <strong>„Abgesagt“</strong> zurück auf{" "}
            <strong>„{absageZurueckZiel ? EVENT_STATUS[absageZurueckZiel].label : ""}“</strong>. Der Gast bekommt dazu keine Mail.
          </p>
          <label className="block font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
            Warum wird die Absage zurückgenommen? (Pflicht, steht im Verlauf)
            <textarea
              className="input-base mt-1"
              rows={3}
              maxLength={1000}
              autoFocus
              value={absageZurueckGrund}
              onChange={(e) => setAbsageZurueckGrund(e.target.value)}
            />
          </label>
          <Zeichenzaehler current={absageZurueckGrund.length} max={1000} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setAbsageZurueckZiel(null)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy || absageZurueckGrund.trim().length < 3}>
              Absage zurücknehmen
            </Button>
          </div>
        </form>
      </Drawer>
      <Drawer open={declineOpen} onClose={() => setDeclineOpen(false)} title="Angebot abgelehnt">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const grund = declineWahl === "__frei__" ? declineFrei : declineWahl;
            setDeclineOpen(false);
            void transition("offer_declined", grund);
          }}
        >
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            Warum hat der Kunde abgesagt? Das hilft dem Assistenten, künftige Angebote besser zu treffen.
          </p>
          <div className="space-y-2">
            {DECLINE_GRUENDE.map((g) => (
              <label key={g} className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                <Radio name="ablehnungsgrund" value={g} checked={declineWahl === g} onChange={() => setDeclineWahl(g)} />
                {g}
              </label>
            ))}
            <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              <Radio name="ablehnungsgrund" value="__frei__" checked={declineWahl === "__frei__"} onChange={() => setDeclineWahl("__frei__")} />
              Anderer Grund
            </label>
            {declineWahl === "__frei__" && (
              <textarea
                className="input-base"
                rows={3}
                maxLength={1000}
                autoFocus
                placeholder="Was war der Grund?"
                value={declineFrei}
                onChange={(e) => setDeclineFrei(e.target.value)}
              />
            )}
            {declineWahl === "__frei__" && <Zeichenzaehler current={declineFrei.length} max={1000} />}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setDeclineOpen(false);
                void transition("offer_declined");
              }}
            >
              Ohne Grund ablehnen
            </Button>
            <Button type="submit" disabled={busy || !declineWahl || (declineWahl === "__frei__" && !declineFrei.trim())}>
              {busy ? "Speichert …" : "Ablehnung speichern"}
            </Button>
          </div>
        </form>
      </Drawer>

      {/* "Erinnerung senden" (09.09.2026, Antoine-Auftrag): Vorschau/Edit VOR dem Versand, statt
          sofort zu senden - s. Kommentar an `onErinnerungGesendet` oben. */}
      {/* Punkt 96: „Telefonisch angenommen“ fragt vor der Gast-Mail. */}
      <TelefonischAngenommenDialog
        open={telefonischOffen}
        onClose={() => setTelefonischOffen(false)}
        eventId={id}
        neueFassung={Boolean(event.offerFassungVeraltetSeit)}
        abgelaufenSeit={ablaufSeit}
        eventDate={event.eventDate ?? null}
        onTerminAendern={() => eckdatenOeffnen(false)}
        onAngenommen={telefonischAngenommen}
        onFehler={telefonischFehler}
      />

      <GueltigkeitVerlaengernDialog
        open={gueltigkeitOffen}
        onClose={() => setGueltigkeitOffen(false)}
        eventId={id}
        gueltigBis={event.offerValidUntil}
        eventDate={event.eventDate ?? null}
        onTerminAendern={() => eckdatenOeffnen(false)}
        onVerlaengert={() => {
          eventQ.reload();
          activityQ.reload();
        }}
      />

      <ErinnerungSendenDialog
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
        eventId={id}
        eventNumber={event.number}
        onSent={onErinnerungGesendet}
        onTerminAendern={() => eckdatenOeffnen(false)}
      />

      {/* Punkt 84 (Antoine, 20.09.2026): „Klick darauf zeigt eine komplette Liste im Popup."
          Die Liste existierte schon — sie war von der Versandzeile aus nur nicht erreichbar.
          Bedingt gerendert statt `id ?? ""`: ohne Vorgangskennung gibt es nichts zu zeigen, und
          ein leerer String waere eine Abfrage nach dem Vorgang „" — ein stiller Fehlschlag. */}
      {id && <AngebotsVerlaufDrawer eventId={id} open={versandVerlaufOffen} onClose={() => setVersandVerlaufOffen(false)} />}

      {/* „Verschieben“ (Antoine, 26.09.2026): Terminvorschlag mit Kalender und Vorschau, OHNE
          Storno — s. `verschiebenSenden` oben und den Kopfkommentar von `VerschiebenDialog`. */}
      {id && (
        <VerschiebenDialog
          open={verschiebenOffen}
          onClose={() => setVerschiebenOffen(false)}
          eventId={id}
          anlass={event.occasion ?? null}
          eventDateIso={event.eventDate ?? null}
          gaeste={event.guestCount ?? null}
          betrieb={companyName || null}
          customerName={customer?.company?.trim() || customer?.name || null}
          customerEmail={customer?.email ?? null}
          companyEmail={settingsQ.data?.companyEmail ?? null}
          onSend={verschiebenSenden}
          onPreview={(subject, text) => openPrintableDocumentPost(`/api/events/${id}/offers/freetext-preview`, { subject, text })}
        />
      )}

      {/* Punkt 94: Rueckfrage bei unterschriebener Kostenuebernahme (oeffnet sich auf die 422 der Storno-Route). */}
      <AbsageKostenuebernahmeDialog
        lage={kuLage}
        onClose={() => setKuLage(null)}
        onAbsagen={(kostenuebernahmeEntscheidung, grund) => stornieren({ kostenuebernahmeEntscheidung, grund })}
      />

      {/* Hard-Delete (30.08.2026, Antoine: "Ja, mit scharfer Warnung"): Bestaetigungs-Modal im
          Stil des Inhaberwechsels (TeamTab, PR #803) + des Builder-Loesch-Modals. Es listet auf,
          was am Vorgang dranhaengt, und verlangt die Vorgangsnummer zum Abtippen — geprueft wird
          sie SERVERSEITIG (422 bei Abweichung, keine Loeschung). */}
      <Modal
        open={hardDeleteOffen}
        onClose={() => {
          if (!hardDeleteBusy) setHardDeleteOffen(false);
        }}
        width="480px"
        gefahr
        title={<>Vorgang {anzeigeNummer} endgültig löschen?</>}
        footer={
          <>
            <Button type="button" variant="secondary" disabled={hardDeleteBusy} onClick={() => setHardDeleteOffen(false)}>
              Abbrechen
            </Button>
            <Button type="button" variant="danger" disabled={hardDeleteBusy || !hardDeleteInput.trim()} onClick={() => void hardDelete()}>
              {hardDeleteBusy ? "Löscht …" : "Endgültig löschen"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Antoine, 26.09.2026: „mit Warnhinweis, dass nur Test und SPAM gelöscht werden sollen,
              nicht reguläre Kunden/Angebote“ — steht ÜBER allem, bevor irgendetwas anderes gelesen wird. */}
          <p
            data-testid="loeschen-warnhinweis"
            className="flex items-start gap-2 rounded-lg border border-error/40 bg-error-container px-3 py-2.5 text-[13px] font-semibold text-error dark:border-error/40 dark:bg-error/10 dark:text-[#e39b92]"
          >
            <Icon name="report" className="mt-px text-[18px]" />
            <span>Nur für Test- und Spam-Anfragen. Echte Kunden und Angebote nicht löschen – dafür Verschieben oder Stornieren.</span>
          </p>
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            Das entfernt den Vorgang und ALLE zugehörigen Daten endgültig — auch Zahlungsdatensätze und
            Rechnungsverweise. Das lässt sich nicht rückgängig machen. Bei echten (Nicht-Test-)Rechnungen bist du
            selbst für die gesetzliche Aufbewahrung verantwortlich.
          </p>
          {/* Was haengt dran? Zahlungen/Versand kennt die Seite; Rechnungen + Vertrag laedt das
              Modal beim Oeffnen nach. Stripe-/LexOffice-/eSign-Objekte existieren beim Anbieter
              weiter — geloescht wird nur der Anker in MAESTRO. */}
          <ul className="space-y-1 rounded-lg border border-outline-variant/40 bg-surface-container-low p-3 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30">
            <li className="flex items-center gap-2">
              <Icon name={event.offerSentAt ? "warning" : "check"} className="text-[15px]" />
              {event.offerSentAt ? `Angebot wurde am ${formatDate(event.offerSentAt)} an den Kunden versendet.` : "Kein Angebot versendet."}
            </li>
            <li className="flex items-center gap-2">
              <Icon name={(paymentsQ.data?.length ?? 0) > 0 ? "warning" : "check"} className="text-[15px]" />
              {(paymentsQ.data?.length ?? 0) > 0
                ? `${paymentsQ.data!.length} Zahlungsdatensatz/-sätze werden mitgelöscht${paymentsQ.data!.some((p) => p.stripePaymentIntentId || p.checkoutUrl) ? " (mit Stripe-Bezug)" : ""}.`
                : "Keine Zahlungen angelegt."}
            </li>
            <li className="flex items-center gap-2">
              <Icon name={(hardDeleteInvoicesQ.data?.length ?? 0) > 0 ? "warning" : "check"} className="text-[15px]" />
              {hardDeleteInvoicesQ.loading
                ? "Rechnungen werden geprüft …"
                : (hardDeleteInvoicesQ.data?.length ?? 0) > 0
                  ? `${hardDeleteInvoicesQ.data!.length} Rechnung(en) werden mitgelöscht (${hardDeleteInvoicesQ.data!.map((i) => i.invoiceNumber ?? i.invoiceType).join(", ")}).`
                  : "Keine Rechnungen angelegt."}
            </li>
            <li className="flex items-center gap-2">
              <Icon name={caQ.data ? "warning" : "check"} className="text-[15px]" />
              {caQ.loading
                ? "Kostenübernahme wird geprüft …"
                : caQ.data
                  ? `Kostenübernahme/Vertrag (${caQ.data.status}) wird mitgelöscht.`
                  : "Keine Kostenübernahme angefordert."}
            </li>
          </ul>
          <label className="block">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Zur Bestätigung: Vorgangsnummer eingeben — „{anzeigeNummer}“
            </span>
            <Input
              value={hardDeleteInput}
              onChange={(e) => setHardDeleteInput(e.target.value)}
              placeholder={anzeigeNummer}
              autoFocus
            />
          </label>
          {hardDeleteFehler && <ErrorNote message={hardDeleteFehler} />}
        </div>
      </Modal>
    </>
  );
}

/**
 * Abrechnung Stufe 1 (Mockup 28.09.2026, Einstieg): „Zur Abrechnung“ öffnet ab dem Tag nach der
 * Feier direkt den Reiter „Abrechnen“, davor wie bisher die „Übersicht“.
 */
function zurAbrechnungPfad(event: { id: string; eventDate?: string | null; dateEnd?: string | null }): string {
  return feierVorbei(event.eventDate, event.dateEnd, heuteInZone("Europe/Berlin")) ? `${abrechnungPfad(event.id)}?reiter=abrechnen` : abrechnungPfad(event.id);
}
